import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import "@/App.css";
import { BrowserRouter } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'sonner';
import { 
  Copy, Check, Loader2, Settings, Separator as SepIcon, ChevronLeft,
} from 'lucide-react';

// UI Components
import { Button } from './components/ui/button';
import { Separator } from './components/ui/separator';

// Auth
import { AuthProvider, useAuth } from './components/AuthProvider';
import AuthModal from './components/AuthModal';
import SiteGate from './components/SiteGate';
import WeekendCatchup from './components/WeekendCatchup';
import OrdersReview from './components/mediscribe/OrdersReview';
import VersionChecker, { APP_VERSION } from './components/VersionChecker';
import OfflineBanner from './components/mediscribe/OfflineBanner';
import OfflineQueuePanel, { OfflineQueueTriggerButton } from './components/mediscribe/OfflineQueuePanel';
import { useOfflineAutoDrain } from './hooks/useOfflineAutoDrain';
import KeyboardCheatsheet from './components/mediscribe/KeyboardCheatsheet';
import IdlePrivacyLock from './components/mediscribe/IdlePrivacyLock';
import BackChartingResumeBanner from './components/mediscribe/BackChartingResumeBanner';
import PatientSetupHeader from './components/mediscribe/PatientSetupHeader';
import PickUpWhereYouLeftOff from './components/mediscribe/PickUpWhereYouLeftOff';
import DailyStreakChip from './components/mediscribe/DailyStreakChip';
import { extractDemographicsFromPaste } from './utils/smartPasteExtractor';

// MediScribe Components
import { 
  Header, Footer, CreatorFooter, ShareModal, MoodInfoModal, SaveTemplateModal, ProfileModal, 
  RoleSelector, PatientTypeSelector, LivePreview, EmptyState, WhatsNextSection,
  NotesInput, PatientDemographics, ProviderDemographics, ProviderICD10Section, ChiefComplaintSection, ICD10Section, DetectedICD10Inline,
  PatientLimitationsHelper, PatientLetterOptions, ProviderOutputOptions,
  ProviderReferralsSection, ProviderProfileSection, ActionButtons, ProviderAdvancedOptions,
  SmartICD10Search, LetterSelector, HistorySlidePanel, RestoreSessionDialog,
  ICD10CodesDisplay, WhatsNextAdvice, SectionNav, OutputDocumentsPanel, HistoryPanel,
  QuickActionsPanel, SmartTemplatesPanel, PatientLabelSection, TeamCollaborationPanel,
  TeamBoardSlidePanel, ShortcutLegend, ImagingOrdersInput, SpecialtyReferralsInput,
  AppModals, ProviderToggleSection, FloatingActions,
} from './components/mediscribe';
import OutputsHeaderRow from './components/mediscribe/OutputsHeaderRow';
import FloatingDictation from './components/mediscribe/FloatingDictation';
import FloatingMicFAB from './components/mediscribe/FloatingMicFAB';
import FloatingTodoWidget from './components/mediscribe/FloatingTodoWidget';
import NetworkDraftBanner from './components/mediscribe/NetworkDraftBanner';
import WhiteSpaceBanner from './components/mediscribe/WhiteSpaceBanner';
import HourglassIcon from './components/mediscribe/HourglassIcon';
import PatientWizard from './components/mediscribe/PatientWizard';
import VAClaimsWizard from './components/mediscribe/VAClaimsWizard';
import PatientProfileSettings from './components/mediscribe/PatientProfileSettings';
import TeamMessageBar from './components/mediscribe/TeamMessageBar';
import BatchChartingGrid from './components/mediscribe/BatchChartingGrid';
import ProviderInputCard from './components/mediscribe/ProviderInputCard';
import EncounterErrorBoundary from './components/mediscribe/EncounterErrorBoundary';
import ColumnScrollNav from './components/mediscribe/ColumnScrollNav';
import CommandPalette from './components/mediscribe/CommandPalette';
import LiveMetricsDashboard from './components/mediscribe/LiveMetricsDashboard';
import HotfixBundleModal, { hasSeenHotfixBundle } from './components/mediscribe/HotfixBundleModal';
import FloatingBugReportButton from './components/mediscribe/FloatingBugReportButton';
import FloatingWidgetDock from './components/mediscribe/FloatingWidgetDock';
import { TeamSettings, TeamDashboardSelector } from './components/TeamSettings';
import TutorialSlideshow from './components/TutorialSlideshow';
import BugReportModal from './components/BugReportModal';

// Constants
import { 
  IMAGING_TYPES, MEDICAL_SPECIALTIES, BODY_PARTS, PATIENT_STATUSES, 
  LETTER_TYPES, MTF_OPTIONS, RANKS, MOOD_THEMES, PROFILE_RESTRICTIONS,
  ENCOUNTER_TYPES, VISIT_TYPES, PANEL_OWNERSHIP, APPOINTMENT_TYPES, REASON_FOR_VISIT_OPTIONS
} from './constants/mediscribe';

// Hooks
import { useLiveIcd10, useImagingSpecialtyExtraction, calculateAge, formatDate, copyToClipboard } from './hooks/useMediscribe';
import { useRecording } from './hooks/useRecording';
import { useHistory } from './hooks/useHistory';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import { useProfile } from './hooks/useProfile';
import { usePatientForm } from './hooks/usePatientForm';
import { useUIPreferences } from './hooks/useUIPreferences';
import { useTemplates } from './hooks/useTemplates';
import { useDocumentOutput } from './hooks/useDocumentOutput';
import { useAutosave } from './hooks/useAutosave';
import { useExportActions } from './hooks/useExportActions';
import useAutosaveSession from './hooks/useAutosaveSession';
import useSessionSharing from './hooks/useSessionSharing';
import useQuickActions from './hooks/useQuickActions';
import useDocumentExport from './hooks/useDocumentExport';
import useSmartTemplates from './hooks/useSmartTemplates';
import useGeneration from './hooks/useGeneration';
import useAutoDetect from './hooks/useAutoDetect';
import useReferralCapture from './hooks/useReferralCapture';
import useFormActions from './hooks/useFormActions';
import useAppState from './hooks/useAppState';
import { smartTitleCase, getAdditionalBillingCodes, generateNoteTitle, extractPatientInfo, extractAppointmentTime } from './hooks/noteUtils';
import { extractBracketFields, getFieldInputType, getFieldPlaceholder, applyFieldValues } from './hooks/bracketFieldUtils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './components/ui/collapsible';
import { ChevronDown } from 'lucide-react';

// Router
import AppRouter, { ErrorBoundary } from './AppRouter';
import { ProviderProductivityReport } from './components/mediscribe/ProviderProductivityReport';
import { useIcd10UsageSync } from './hooks/useIcd10UsageSync';

const BACKEND_URL = window.__WR_API_BASE__;
const API = `${BACKEND_URL}/api`;

// Generate or retrieve persistent guest ID
const getGuestId = () => {
  let guestId = localStorage.getItem('writeright_guest_id');
  if (!guestId) {
    guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('writeright_guest_id', guestId);
  }
  return guestId;
};

const MediScribe = () => {
  const { user, isGuest, isAuthenticated, logout, updateProfile, loading: authLoading } = useAuth();

  // Feb 28, 2026 — server-mirror ICD-10 chip tap counts so the freq-sorted
  // chip ordering follows the user across devices. Debounced background
  // POST; no UI surface.
  useIcd10UsageSync(user);

  const guestId = useMemo(() => {
    if (user && user.user_id) return null;
    return getGuestId();
  }, [user]);

  // Apply minimalist tile mode on boot if user opted in (Feb 26, 2026).
  // Sets a data attr on <html> that CSS keys off of.
  useEffect(() => {
    try {
      const v = localStorage.getItem('writeright_minimalist_tiles') === '1' ? '1' : '0';
      document.documentElement.setAttribute('data-minimalist', v);
    } catch { /* ignore */ }
  }, []);
  
  // User Role State
  const [userRole, setUserRole] = useState(() => localStorage.getItem('writeright_role') || null);
  const [patientType, setPatientType] = useState(() => localStorage.getItem('writeright_patient_type') || null);
  const [isRoleSwitching, setIsRoleSwitching] = useState(false);
  const effectiveRole = userRole || null;

  // Auto-resume Provider mode if "Skip Role Selector on Login" toggle is enabled.
  // Respects explicit switchRole (isRoleSwitching=true) so the user can still get back
  // to the selector on-demand without disabling the toggle.
  useEffect(() => {
    if (isAuthenticated && !isGuest && !userRole && !isRoleSwitching) {
      if (localStorage.getItem('writeright_skip_role_selector') === 'true') {
        setUserRole('provider');
        localStorage.setItem('writeright_role', 'provider');
      }
    }
  }, [isAuthenticated, isGuest, userRole, isRoleSwitching]);

  // Mar 2, 2026 — Fresh-login state reset. When the auth state flips from
  // unauthenticated → authenticated (a real sign-in, not a refresh), clear
  // any stale patientType / letterType / documentMode that a PREVIOUS user
  // (or a prior session's Quick Letter tap) persisted to localStorage.
  // Without this, provider users get silently dropped into a patient letter
  // form because `patientType='active_duty'` + `letterType='fmla'` were
  // still in storage from days ago. The RoleSelector or Provider dashboard
  // should always be the first surface post-login.
  const wasAuthRef = React.useRef(isAuthenticated);
  useEffect(() => {
    const prev = wasAuthRef.current;
    wasAuthRef.current = isAuthenticated;
    if (!prev && isAuthenticated && !isGuest) {
      try {
        localStorage.removeItem('writeright_patient_type');
        localStorage.removeItem('writeright_letter_type');
        localStorage.removeItem('writeright_document_mode');
      } catch { /* ignore */ }
      setPatientType(null);
      // If the Skip-Role-Selector toggle is OFF, also reset the userRole so
      // the RoleSelector shows on every login.
      if (localStorage.getItem('writeright_skip_role_selector') !== 'true') {
        localStorage.removeItem('writeright_role');
        setUserRole(null);
      }
    }
  }, [isAuthenticated, isGuest]);
  
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [legalPage, setLegalPage] = useState(null);
  const [showUpdateReminder, setShowUpdateReminder] = useState(() => {
    return localStorage.getItem('writeright_seen_version') !== APP_VERSION;
  });
  
  // Profile hook
  const profile = useProfile(user, updateProfile);
  // Mar 3, 2026 — Refer-a-Provider: capture ?ref=<code> on landing, ping the
  // backend, and stash for signup attribution. No-op if no ref param.
  useReferralCapture();
  const {
    showProfileModal, setShowProfileModal,
    profileSignature, setProfileSignature, profileMTF, setProfileMTF, profileRank, setProfileRank,
    isSavingProfile,
    letterheadLine1, setLetterheadLine1, letterheadLine2, setLetterheadLine2, letterheadAddress, setLetterheadAddress,
    defaultMood, setDefaultMood, defaultTextSize, setDefaultTextSize,
    defaultIncludeROS, setDefaultIncludeROS, defaultIncludePE, setDefaultIncludePE,
    defaultMedicMode, setDefaultMedicMode,
    defaultVisitType, setDefaultVisitType, defaultInputMode, setDefaultInputMode,
    visitSchedule, setVisitSchedule,
    defaultLetterTone, setDefaultLetterTone,
    personalFirstName, setPersonalFirstName, personalLastName, setPersonalLastName,
    personalDOB, setPersonalDOB, personalPhone, setPersonalPhone, personalOccupation, setPersonalOccupation,
    // v6.8.44 — Saved Medical History / Injuries
    savedInjuries, setSavedInjuries,
    saveProfile: saveProfileToServer,
    handleDefaultVisitTypeChange, handleDefaultInputModeChange,
  } = profile;

  // Panel / modal states
  const [showTeamSettings, setShowTeamSettings] = useState(false);
  // Feb 28, 2026 — Provider Productivity Report (OER-ready printable).
  const [showProductivityReport, setShowProductivityReport] = useState(false);
  // Feb 28, 2026 — Format-Lock on A/P. When ON, regenerating after a manual
  // edit sends `format_lock_ap: true` so the LLM does NOT reorder bullets /
  // re-indent / merge or split items. Persisted in localStorage; bound to
  // a pill in OutputDocumentsPanel that the user toggles encounter-by-
  // encounter or sets once and forgets.
  const [formatLockAp, setFormatLockAp] = useState(() => {
    try { return localStorage.getItem('writeright_format_lock_ap') === 'true'; }
    catch { return false; }
  });
  useEffect(() => {
    const sync = () => {
      try { setFormatLockAp(localStorage.getItem('writeright_format_lock_ap') === 'true'); }
      catch { /* ignore */ }
    };
    window.addEventListener('writeright:format-lock-changed', sync);
    return () => window.removeEventListener('writeright:format-lock-changed', sync);
  }, []);
  const [myProviders, setMyProviders] = useState([]);
  const [showHistorySlidePanel, setShowHistorySlidePanel] = useState(false);
  const [showOfflineQueuePanel, setShowOfflineQueuePanel] = useState(false);
  // Auto-drain queued offline drafts when connectivity returns
  useOfflineAutoDrain();
  const [showTeamBoardPanel, setShowTeamBoardPanel] = useState(false);
  const [teamUnreadCount, setTeamUnreadCount] = useState(0);

  // Background poll for team unread messages — updates header badge
  useEffect(() => {
    if (!user) return;
    const checkUnread = async () => {
      try {
        const res = await axios.get(`${API}/team/messages?limit=1`, { withCredentials: true });
        setTeamUnreadCount(res.data.unread_count || 0);
      } catch { /* silent */ }
    };
    checkUnread(); // check immediately on login
    const interval = setInterval(checkUnread, 45000); // every 45s
    return () => clearInterval(interval);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showShortcutLegend, setShowShortcutLegend] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [showShipVersion, setShowShipVersion] = useState(false);
  // Lifted from OutputDocumentsPanel so the HEADER Copy All can include the
  // full signature block (attestation + CPT + timestamps + sig) — not just
  // the raw provider name + credentials.
  const [fullSignatureBlock, setFullSignatureBlock] = useState('');
  const [showTutorialSlideshow, setShowTutorialSlideshow] = useState(null);
  const [showBatchCharting, setShowBatchCharting] = useState(false);
  const [showPatientProfile, setShowPatientProfile] = useState(false);
  const [initialWizardStep, setInitialWizardStep] = useState(0);

  // Reset initial wizard step after it's been consumed
  useEffect(() => {
    if (initialWizardStep > 0 && patientType) {
      const t = setTimeout(() => setInitialWizardStep(0), 500);
      return () => clearTimeout(t);
    }
  }, [initialWizardStep, patientType]);
  const [showTeamCollab, setShowTeamCollab] = useState(false);
  const [showImagingSection, setShowImagingSection] = useState(false);
  const [showReferralsSection, setShowReferralsSection] = useState(false);
  const [showLabsSection, setShowLabsSection] = useState(false);
  const [labOrders, setLabOrders] = useState('');
  const [medsRxd, setMedsRxd] = useState(false);
  // Feb 28, 2026 — structured Rx detail string (built from MedsChipInput
  // chips). Round-trips alongside the boolean so the LLM gets the
  // structured Rx list when present and the boolean still controls
  // the "yes meds prescribed" branch.
  const [medsRxdDetail, setMedsRxdDetail] = useState('');

  // ─────────────────────────────────────────────────────────────────
  // Feb 28, 2026 (v6.8.43) — Auto-Seed Explainer Map
  // ─────────────────────────────────────────────────────────────────
  // Per-chip rationale strings. Keys are stable chip identifiers:
  //   - `specialty:Neurology` for specialty referral chips
  //   - `imaging:X-Ray` for imaging chips (type-only, since auto-seed
  //     leaves bodyPart blank)
  // Values are 1-line "Why?" captions shown under the chip:
  //   "Auto-added · matched 'migraine' in dictation"
  // The user can dismiss an individual caption (× clears just that
  // explainer, NOT the chip). Cleared automatically on Next Patient.
  const [autoSeedReasons, setAutoSeedReasons] = useState({});
  const clearAutoSeedReason = React.useCallback((key) => {
    setAutoSeedReasons((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);
  useEffect(() => {
    const handler = () => setAutoSeedReasons({});
    window.addEventListener('writeright:next-patient', handler);
    return () => window.removeEventListener('writeright:next-patient', handler);
  }, []);

  // UI Preferences hook
  const uiPrefs = useUIPreferences(MOOD_THEMES);
  const {
    currentMoodIndex, setCurrentMoodIndex, currentMood, favoriteMoods, setFavoriteMoods,
    showMoodInfo, setShowMoodInfo,
    textSizeLevel, setTextSizeLevel, textSizeClass,
    forceSingleColumn, isLargeScreen, toggleColumnLayout,
    readingWidth, setReadingWidth, readingWidthPx,
    inputMode, setInputMode, showSettings, setShowSettings, showAdvanced, setShowAdvanced,
    quickActions, setQuickActions, showQuickActions, setShowQuickActions,
    smartTemplates, setSmartTemplates, showSmartTemplates, setShowSmartTemplates,
    isLoadingSmartTemplates, setIsLoadingSmartTemplates,
  } = uiPrefs;

  // Notes state
  const [notes, setNotes] = useState('');
  const [notesHistory, setNotesHistory] = useState([]);
  const maxNotesHistoryLength = 20;
  
  const saveNotesForUndo = useCallback((currentNotes) => {
    if (currentNotes && currentNotes.trim()) {
      setNotesHistory(prev => {
        const newHistory = [...prev, currentNotes];
        return newHistory.length > maxNotesHistoryLength ? newHistory.slice(-maxNotesHistoryLength) : newHistory;
      });
    }
  }, [maxNotesHistoryLength]);
  
  const undoNotes = useCallback(() => {
    if (notesHistory.length > 0) {
      const previousNotes = notesHistory[notesHistory.length - 1];
      setNotesHistory(prev => prev.slice(0, -1));
      setNotes(previousNotes);
      toast.success('Notes restored', { duration: 1500 });
    } else {
      toast.info('Nothing to undo', { duration: 1500 });
    }
  }, [notesHistory]);
  
  // Recording hook
  const recording = useRecording({ notes, setNotes });
  
  // Patient form state
  const patientForm = usePatientForm();
  const {
    patientAge, setPatientAge, patientGender, setPatientGender,
    patientStatus, setPatientStatus, patientName, setPatientName,
    chiefComplaint, setChiefComplaint,
    patientLabel, setPatientLabel, clinicSchedule, setClinicSchedule,
    autoFilledFields, setAutoFilledFields,
    imagingOrders, setImagingOrders, specialties, setSpecialties,
    manualIcd10, setManualIcd10, primaryIcd10Codes, setPrimaryIcd10Codes,
    addSpecialty, removeSpecialty, updateSpecialty, restoreSpecialty,
    addImaging, removeImaging, updateImagingType, updateImagingBodyPart, updateImagingIndication, restoreImaging,
    dismissedSpecialties, dismissedImaging,
    charLimit, setCharLimit, letterType, setLetterType,
    letterContext, setLetterContext, includeCoverLetter, setIncludeCoverLetter,
    customLetterDescription, setCustomLetterDescription,
    providerLetterTone, setProviderLetterTone,
    includePatientQuotes, setIncludePatientQuotes,
    clinicalTone, setClinicalTone,
    includeROS, setIncludeROS, includePE, setIncludePE,
    peUnremarkable, setPeUnremarkable,
    peExceptions, setPeExceptions,
    medicMode, setMedicMode,
    priorVisitContext, setPriorVisitContext,
    prepNotes, setPrepNotes,
    encounterType, setEncounterType, visitType, setVisitType,
    panelOwnership, setPanelOwnership, appointmentType, setAppointmentType,
    soldierRank, setSoldierRank, soldierFirstName, setSoldierFirstName,
    soldierLastName, setSoldierLastName, soldierDob, setSoldierDob,
    soldierUnit, setSoldierUnit, soldierPhone, setSoldierPhone,
    mtfName, setMtfName, providerSignatureBlock, setProviderSignatureBlock,
    customMtf, setCustomMtf,
    occupation, setOccupation, jobDuties, setJobDuties,
    generateIndication, setGenerateIndication, generateClinicalNote, setGenerateClinicalNote,
    generateAdminLetter, setGenerateAdminLetter, generateLimitations, setGenerateLimitations,
    includeEmAnalysis, setIncludeEmAnalysis,
    apFormat, setApFormat,
    letterTone, setLetterTone, letterPageLimit, setLetterPageLimit,
    letterFormat, setLetterFormat, referencePosition, setReferencePosition,
    documentMode, setDocumentMode, messageScenario, setMessageScenario,
    consultRecipient, setConsultRecipient, consultQuestion, setConsultQuestion,
    selectedRestriction, setSelectedRestriction,
    showRestrictionsHelper, setShowRestrictionsHelper,
    resetPatientForm,
    deferredTopics, setDeferredTopics,
  } = patientForm;

  const [reasonForVisit, setReasonForVisit] = useState('other');
  // App-wide UI/encounter state — extracted to useAppState (Move 3, Feb 26, 2026)
  const {
    virtualModality, setVirtualModality,
    dosOverride, setDosOverride,
    dosTimeOverride, setDosTimeOverride,
    // v6.8.48 — Late-entry checkbox
    lateEntry, setLateEntry,
    customReasonForVisit, setCustomReasonForVisit,
    language, setLanguage,
    currentDraftId, setCurrentDraftId,
    autoSavedDraft, setAutoSavedDraft,
  } = useAppState();

  // Template state
  const templateHook = useTemplates(user, effectiveRole);
  const {
    templates, userTemplates, setUserTemplates,
    selectedTemplate, setSelectedTemplate,
    showTemplates, setShowTemplates,
    showSaveTemplateModal, setShowSaveTemplateModal,
    newTemplateName, setNewTemplateName, newTemplateDesc, setNewTemplateDesc,
    saveAsTemplate: saveAsTemplateHook, deleteUserTemplate,
  } = templateHook;
  
  // Document output state
  const docOutput = useDocumentOutput();
  const {
    isGenerating, setIsGenerating,
    result, setResult,
    documentsReady, setDocumentsReady,
    generationVersion, setGenerationVersion,
    copiedField, setCopiedField,
    activeOutputTab, setActiveOutputTab,
    lastShareUrl, setLastShareUrl,
    showBracketModal, setShowBracketModal,
    bracketFields, setBracketFields,
    pendingGeneration, setPendingGeneration,
    showPreGenModal, setShowPreGenModal,
    preGenFields, setPreGenFields,
    showConflictModal, setShowConflictModal,
    conflicts, setConflicts,
    clearResults,
  } = docOutput;
  
  // History state
  const historyHook = useHistory(user, effectiveRole, guestId);
  const {
    showHistory, setShowHistory,
    history, setHistory, isLoadingHistory,
    selectedHistoryItem, setSelectedHistoryItem,
    batchSelectedItems, setBatchSelectedItems,
    isBatchMode, setIsBatchMode,
    historyFilter, setHistoryFilter,
    historySearchQuery, setHistorySearchQuery,
    dateJump, setDateJump,
    sortOrder, setSortOrder,
    showWeekendCatchup, setShowWeekendCatchup,
    showOrdersReview, setShowOrdersReview,
    condenseVersions, setCondenseVersions,
    expandedGroups, setExpandedGroups,
    historyShareCode, filteredHistory,
    loadHistory, getFilteredHistory,
    toggleBatchSelect, selectAllHistory, exitBatchMode,
    batchCopyNotesAndICD10s, toggleGroupExpanded,
    startShareHistory, getPendingProposals, getReadyForSignature, getPendingDraftsCount,
    clearSelectedHistoryItem,
    deleteHistoryItem, batchDeleteItems, batchExportItems,
    quickExportToday, quickExportDrafts, markDraftsAsCompleted, updateHistoryItem,
  } = historyHook;

  // Live ICD-10 manual order state (must be declared before useFormActions)
  const [manualIcd10Order, setManualIcd10Order] = useState(null);

  // ── Auto-detect hook ──
  const autoDetect = useAutoDetect({
    notes, effectiveRole, user,
    reasonForVisit, setReasonForVisit,
    customReasonForVisit, setCustomReasonForVisit,
    soldierRank, setSoldierRank, soldierDob,
    patientAge, setPatientAge, patientGender, setPatientGender,
    patientStatus, setPatientStatus, chiefComplaint, setChiefComplaint,
    autoFilledFields, setAutoFilledFields,
    patientLabel, setPatientLabel,
    setSoldierFirstName, setSoldierLastName, setSoldierDob,
    setSoldierUnit, setSoldierPhone, setOccupation, setJobDuties,
    includePE, peExceptions, setPeExceptions,
    visitType,
    manualIcd10, setManualIcd10,
  });
  const { reasonAutoDetected, sortedReasonOptions, trackFeature,
          setReasonManually, setCustomReasonManually, resetAutoDetect,
          markPeFindingsManual } = autoDetect;

  // ── Pickup metadata (relay-race audit trail pill above the notes input) ──
  // Tracks who shared the active draft and when, so the provider sees at a
  // glance that this note came from a teammate. Cleared on clearAll/Next Patient.
  const [pickupMeta, setPickupMeta] = useState(null);
  React.useEffect(() => {
    const clear = () => setPickupMeta(null);
    window.addEventListener('writeright:clear-cpts', clear);
    window.addEventListener('writeright:next-patient', clear);
    return () => {
      window.removeEventListener('writeright:clear-cpts', clear);
      window.removeEventListener('writeright:next-patient', clear);
    };
  }, []);

  // ── Command Palette (⌘K / Ctrl+K) — IDE-style fuzzy-search action launcher ──
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showLiveMetrics, setShowLiveMetrics] = useState(false);
  // ── Hotfix Bundle changelog popup (one-time per device per release) ──
  const [showHotfixBundle, setShowHotfixBundle] = useState(false);
  React.useEffect(() => {
    const handler = (e) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key === 'k';
      if (isCmdK) {
        e.preventDefault();
        setShowCommandPalette((s) => !s);
      }
    };
    const openHandler = () => setShowCommandPalette(true);
    window.addEventListener('keydown', handler);
    window.addEventListener('writeright:open-command-palette', openHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('writeright:open-command-palette', openHandler);
    };
  }, []);

  // Surface the May 6 Production Hotfix Bundle once per device per release.
  // Trigger only after a role is selected so we don't compete with the NDA
  // gate / role selector / restore dialog. ~1.5s delay lets the workspace
  // render first so the popup feels intentional, not interruptive.
  React.useEffect(() => {
    if (!effectiveRole) return;
    if (hasSeenHotfixBundle()) return;
    const t = setTimeout(() => setShowHotfixBundle(true), 1500);
    return () => clearTimeout(t);
  }, [effectiveRole]);

  // ── Session sharing (before formActions — provides shareCode/updateShareSession) ──
  const {
    shareCode, isSharing,
    showShareModal, setShowShareModal,
    startShareSession, updateShareSession, endShareSession,
  } = useSessionSharing({
    notes, patientAge, patientGender, patientStatus,
    imagingOrders, specialties, result,
  });

  // Auto-stream to default team on Provider mount — silently ENABLES team
  // auto-sharing if the "Auto-Stream to Default Team" Profile toggle is on AND
  // a default team is selected. Does NOT create a temp share link (that path
  // was the bug — it produced a one-off code/URL on every note instead of
  // streaming to the persistent team). The actual per-note share to the team
  // happens elsewhere via the existing `autoShareDrafts` mechanism in
  // `useAutosaveSession.shareDraftToTeam`.
  // Feb 28, 2026 — also depend on `user?.default_auto_stream` so this effect
  // re-runs AFTER useProfile syncs the server preference into localStorage.
  // Previously it only ran once on role change → on a fresh device login,
  // localStorage was empty when this effect first ran, so the toast and
  // toggle activation never fired even after the server value loaded.
  React.useEffect(() => {
    if (effectiveRole !== 'provider') return;
    try {
      const enabled = localStorage.getItem('writeright_auto_stream_default') === 'true'
        || user?.default_auto_stream === true;
      // Feb 22, 2026 — fall back to `writeright_active_team` if the dedicated
      // auto-share-team key isn't populated. Eliminates the "select team every
      // login" bug where the toggle stayed on but the team slot was empty.
      let teamId = localStorage.getItem('writeright_auto_share_team');
      if (!teamId) {
        teamId = localStorage.getItem('writeright_active_team');
        // Promote it so future surfaces see it without the fallback.
        if (teamId) localStorage.setItem('writeright_auto_share_team', teamId);
      }
      if (!enabled) return;
      // If autoShareDrafts isn't already on, silently enable it for this session.
      const currentlyOn = localStorage.getItem('writeright_auto_share_drafts') === 'true';
      if (!currentlyOn) {
        localStorage.setItem('writeright_auto_share_drafts', 'true');
        const teamName = localStorage.getItem('writeright_active_team_name') || 'team';
        // If we have a team selected, name it. Otherwise just confirm global ON.
        toast.success(
          teamId ? `Streaming notes to ${teamName}` : 'Auto-stream ON — sharing to all your teams',
          { duration: 2500 }
        );
      }
    } catch { /* localStorage unavailable */ }
  }, [effectiveRole, user?.default_auto_stream]);

  // ── Form actions hook ──
  const formActions = useFormActions({
    notes, setNotes, saveNotesForUndo,
    setImagingOrders, setSpecialties,
    setPatientAge, setPatientGender, setPatientStatus, setChiefComplaint, setManualIcd10,
    setPrimaryIcd10Codes, setPatientLabel, setPatientName, setAppointmentType,
    // Feb 28, 2026 — reset reason-for-appointment between encounters (bug fix).
    setReasonForVisit, setCustomReasonForVisit,
    setLetterType, setLetterContext, setSelectedTemplate,
    setResult, setAutoFilledFields,
    setSoldierRank, setSoldierFirstName, setSoldierLastName, setSoldierDob, setSoldierUnit, setSoldierPhone,
    setOccupation, setJobDuties, setIncludeCoverLetter,
    setGenerateIndication, setGenerateClinicalNote, setGenerateAdminLetter, setGenerateLimitations,
    currentDraftId, setCurrentDraftId,
    setAutoSavedDraft, setDocumentsReady,
    recording, setSelectedHistoryItem, setActiveOutputTab, setLastShareUrl,
    showHistory, loadHistory,
    shareCode, updateShareSession,
    patientAge, patientGender, patientStatus, chiefComplaint,
    imagingOrders, specialties, manualIcd10, guestId,
    setUserRole, setPatientType, setIsRoleSwitching, effectiveRole,
    providerSignatureBlock, mtfName, customMtf,
    setProviderSignatureBlock, setMtfName, setCustomMtf,
    defaultVisitType, setVisitType, isGenerating,
    resetAutoDetect, MTF_OPTIONS,
    clinicSchedule, setClinicSchedule,
    setDeferredTopics,
    setManualIcd10Order,
    setPriorVisitContext,
    setPrepNotes,
    setLabOrders, setShowLabsSection, setMedsRxd,
    setGenerationVersion,
    setIncludeROS, setIncludePE, setPeUnremarkable, setPeExceptions,
    setShowImagingSection, setShowReferralsSection, setIncludeEmAnalysis,
    setDosOverride,
    // v6.8.48 — late entry + DOS time reset on Next Patient
    setDosTimeOverride,
    setLateEntry,
    setBracketFields,
  });

  // Wire autosave with restoreFormData from formActions
  const {
    autoShareTeamId, autoShareDrafts,
    lastAutoSave,
    showRestoreDialog, pendingRestoreData,
    handleRestoreChoice: handleRestoreChoiceBase,
    toggleAutoShareTeam, toggleAutoShareDrafts,
    quickShareToTeam, shareDraftToTeam,
    saveGuestSession, restoreGuestSession,
    buildAutosaveData,
    globalAutoStreamOn, userTeams,
  } = useAutosaveSession({
    notes, result, isGenerating, effectiveRole, guestId,
    imagingOrders, specialties, patientAge, patientGender, patientStatus,
    chiefComplaint, manualIcd10, charLimit, letterType, letterContext,
    soldierRank, soldierFirstName, soldierLastName, soldierDob, soldierUnit, soldierPhone,
    mtfName, customMtf, providerSignatureBlock, encounterType, visitType,
    includeROS, includePE, generateClinicalNote, generateAdminLetter, generateLimitations,
    occupation, jobDuties, selectedTemplate, patientLabel,
    user, loadHistory, trackFeature, restoreFormData: formActions.restoreFormData,
    currentDraftId, setCurrentDraftId, autoSavedDraft, setAutoSavedDraft,
  });

  // Document export
  const {
    handleCopy, exportToWord, exportToPDF, shareLetter,
    fillLetterPlaceholders, getFilledLetter, copyAllDocuments,
    copyClinicalNoteOnly, copyOrdersOnly,
  } = useDocumentExport({
    setCopiedField, setLastShareUrl,
    providerSignatureBlock, letterheadLine1, letterheadLine2, letterheadAddress,
    soldierPhone, setSoldierPhone, soldierRank, soldierFirstName, soldierLastName,
    soldierDob, soldierUnit, letterType, mtfName, customMtf,
    showHistory, loadHistory,
  });

  const handleRestoreChoice = (choice) => {
    if (choice === 'done') formActions.clearAll(true);
    if (choice === 'history') { setShowHistory(true); setTimeout(() => { document.querySelector('[data-testid="history-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 300); }
    handleRestoreChoiceBase(choice);
  };

  // ICD-10: Auto-detect for display-only suggestions. Only manualIcd10 goes to note.
  const autoDetectedCodes = useLiveIcd10(notes);
  const liveIcd10Suggestions = manualIcd10Order || [];
  useImagingSpecialtyExtraction(notes, setImagingOrders, setSpecialties, imagingOrders, specialties, visitType, dismissedSpecialties, dismissedImaging);

  // Sync PE defaults + auto-suggest ICD-10 + auto-toggle sections when visit type changes
  const prevVisitTypeRef = React.useRef(visitType);
  useEffect(() => {
    const prev = prevVisitTypeRef.current;
    prevVisitTypeRef.current = visitType;
    if (prev === visitType) return;
    const isVirtual = visitType === 'virtual' || visitType === 'results_review_virtual';
    const wasVirtual = prev === 'virtual' || prev === 'results_review_virtual';
    if (isVirtual && !wasVirtual) {
      setIncludePE(false);
      setPeUnremarkable(false);
      // Feb 27, 2026 (v6.8.23) — REMOVED auto-check of Referrals on virtual
      // switch. Provider feedback: "it's defaulting to add a referral when
      // not checked or stated in the boxes." The auto-check was a helpful
      // guess (virtual visits often end in referrals) but it added rows
      // unexpectedly when the visit-type toggle flipped — including via
      // the new modality auto-detect from notes ("vh" → virtual). Let the
      // provider explicitly check Referrals in the header row instead.
    } else if (!isVirtual && wasVirtual) {
      setIncludePE(true);
      setPeUnremarkable(true);
      // Symmetric removal — don't auto-uncheck on virtual→in-person either.
    }
    // Auto-suggest visit-type ICD-10 codes
    const addCode = (code) => {
      const current = (manualIcd10 || '').split(',').map(c => c.trim()).filter(Boolean);
      if (!current.includes(code)) {
        setManualIcd10([...current, code].join(', '));
      }
    };
    if (visitType === 'results_review' || visitType === 'results_review_virtual') {
      addCode('Z71.2');
    }
    if (visitType === 'med_refill') {
      addCode('Z76.0');
    }
  }, [visitType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-suggest ICD-10 when reason for visit changes
  const prevReasonRef = React.useRef(reasonForVisit);
  useEffect(() => {
    const prev = prevReasonRef.current;
    prevReasonRef.current = reasonForVisit;
    if (prev === reasonForVisit) return;
    const addCode = (code) => {
      const current = (manualIcd10 || '').split(',').map(c => c.trim()).filter(Boolean);
      if (!current.includes(code)) {
        setManualIcd10([...current, code].join(', '));
      }
    };
    // Feb 18, 2026 — reasonForVisit can now be a comma-separated multi-select
    // (e.g. "annual_exam,lab_review"). Split and react to ALL selected reasons.
    const reasons = (reasonForVisit || '').split(',').map(r => r.trim()).filter(Boolean);
    if (reasons.includes('consult_follow_up')) addCode('Z09');
    if (reasons.includes('lab_review') || reasons.includes('imaging_review')) addCode('Z71.2');
    if (reasons.includes('medication_refill')) addCode('Z76.0');
  }, [reasonForVisit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────
  // Feb 28, 2026 (v6.8.42) — Condition Auto-Seed Engine
  // ─────────────────────────────────────────────────────────────────
  // Pattern-driven replacement for the single-purpose MSK helper from
  // v6.8.40. Each pattern declares its trigger (reasonForVisit value
  // and/or positive-mention regex with a negation guard) plus the
  // chips/tips it wants to surface. A single ref-Set tracks which
  // patterns have already fired so we never re-seed the same pattern
  // within a single encounter. The Set clears on Next Patient.
  //
  // Adding a new pattern? Push to AUTO_SEED_PATTERNS — no other code
  // changes needed.
  const AUTO_SEED_PATTERNS = React.useMemo(() => ([
    {
      id: 'msk',
      reason: 'msk',
      regex: /\bjoint[-\s]?pains?\b/i,
      negation: /\b(no|denies|negative for|without)\s+joint[-\s]?pains?\b/i,
      referral: 'Orthopedic Surgery',
      imaging: { type: 'X-Ray', bodyPart: '', indication: '' },
      reasonToast: 'MSK visit — Ortho referral + X-Ray pre-seeded (fill in body part).',
      dictationToast: 'Joint pain detected — Ortho referral + X-Ray pre-seeded (fill in body part).',
    },
    {
      // GU complaints — dysuria, hematuria, frequency, UTI, AND male
      // sexual-dysfunction (erectile dysfunction, ED, impotence). Both
      // funnel to Urology. No imaging by default — Urology drives that
      // workup themselves (US, CT, cystoscopy as indicated).
      id: 'gu_urology',
      // No specific REASON_FOR_VISIT_OPTIONS value for GU yet; pattern
      // matches dictation only.
      reason: null,
      regex: /\b(erectile dysfunction|impotence|ED\b|dysuria|burning\s+urination|hematuria|UTI\b|recurrent\s+urinary|urinary\s+frequency|urinary\s+urgency)\b/i,
      negation: /\b(no|denies|negative for|without)\s+(erectile dysfunction|impotence|ED|dysuria|hematuria|UTI|urinary)\b/i,
      referral: 'Urology',
      imaging: null,
      dictationToast: 'GU complaint detected — Urology referral pre-seeded.',
    },
    {
      // Headache / migraine → Neurology. No imaging on the FIRST pass —
      // most primary headache doesn't need MRI; we let the provider add
      // CT/MRI manually if red-flag features (thunderclap, focal deficit,
      // post-trauma) are present.
      id: 'headache_neuro',
      reason: null,
      regex: /\b(headache|migraines?|cephalgia|HA\s*[×x]?\s*\d|chronic\s+HA)\b/i,
      negation: /\b(no|denies|negative for|without)\s+(headache|migraines?|HA)\b/i,
      referral: 'Neurology',
      imaging: null,
      dictationToast: 'Headache pattern detected — Neurology referral pre-seeded.',
    },
    {
      // Chest WALL pain (costochondritis / Tietze) — NSAID first-line,
      // no referral, no imaging. Distinct from cardiac chest pain
      // because the pattern requires the "wall" qualifier or specific
      // costo terms — this pattern intentionally does NOT fire on
      // "chest pain" alone (cardiac differential needs provider-driven
      // workup, not auto-suggestion).
      id: 'costo_nsaid',
      reason: null,
      regex: /\b(chest\s+wall\s+pain|costochondritis|tietze|reproducible\s+chest\s+pain|tender(ness)?\s+(at|over)\s+(the\s+)?costo)/i,
      negation: /\b(no|denies|negative for|without)\s+(chest\s+wall\s+pain|costochondritis|tietze)\b/i,
      referral: null,
      imaging: null,
      tip: 'Costochondritis — consider NSAID (e.g., ibuprofen 600 mg q8h × 7-10 d) + topical diclofenac. No referral typically needed.',
      dictationToast: 'Chest wall pain detected — NSAID tip in your toast tray.',
    },
    {
      // Mental Health — BH referral + SSRI tip + reminder about the
      // in-house BH consultant portal message. Fires on reason=='mental_health'
      // or positive mention in dictation.
      id: 'mental_health',
      reason: 'mental_health',
      regex: /\b(depression|major\s+depressive|MDD\b|anxiety|GAD\b|panic\s+attacks?|PHQ[-\s]?\d|mood\s+disorder|suicidal\s+ideation|SI\b)/i,
      negation: /\b(no|denies|negative for|without)\s+(depression|anxiety|suicidal|MDD|GAD|panic)/i,
      referral: 'Behavioral Health',
      imaging: null,
      tip: 'Consider SSRI first-line (sertraline 25→50 mg, escitalopram 10 mg) + portal message to your in-house BH consultant (Mental Health team). PHQ-9 / GAD-7 at follow-up.',
      reasonToast: 'Mental Health visit — BH referral + SSRI/MH consultant tip pre-seeded.',
      dictationToast: 'Mental health symptoms detected — BH referral + SSRI/MH consultant tip.',
    },
  ]), []);

  const autoSeededRef = React.useRef(new Set());
  useEffect(() => {
    const handler = () => { autoSeededRef.current.clear(); };
    window.addEventListener('writeright:next-patient', handler);
    return () => window.removeEventListener('writeright:next-patient', handler);
  }, []);

  useEffect(() => {
    const reasons = (reasonForVisit || '').split(',').map((r) => r.trim().toLowerCase()).filter(Boolean);
    const lower = (notes || '').toLowerCase();

    AUTO_SEED_PATTERNS.forEach((pat) => {
      if (autoSeededRef.current.has(pat.id)) return;
      const reasonMatch = pat.reason && reasons.includes(pat.reason);
      const dictationMatch = pat.regex && pat.regex.test(notes || '') &&
                              !(pat.negation && pat.negation.test(lower));
      if (!reasonMatch && !dictationMatch) return;
      // Require dictation to settle (≥ 10 chars) before triggering from
      // notes-only. Reason-based triggers fire immediately.
      if (!reasonMatch && (notes || '').length < 10) return;

      // Build a human-readable "why" string for the chip caption.
      // Reason-based:   "MSK (Musculoskeletal) reason selected"
      // Dictation:      "matched 'migraine' in dictation"
      let why;
      if (reasonMatch) {
        why = `${pat.reason.replace(/_/g, ' ')} reason selected`;
      } else {
        const m = (notes || '').match(pat.regex);
        const snippet = m ? m[0] : pat.id;
        why = `matched "${snippet}" in dictation`;
      }

      // Apply the referral chip.
      // Mar 3, 2026 — Race-safe dedupe: move the `has` check INSIDE the
      // functional setState so it always sees the freshest `prev` (not the
      // stale-closure `specialties`). Prevents duplicate chips when two
      // auto-seed sources fire in the same render batch.
      let didReferral = false;
      if (pat.referral) {
        setSpecialties((prev) => {
          const alreadyHas = (prev || []).some((s) => {
            if (typeof s !== 'string') return false;
            return s.split('||')[0].trim() === pat.referral;
          });
          if (alreadyHas) return prev;
          didReferral = true;
          const cleaned = (prev || []).filter((s) => s && (typeof s !== 'string' || s.trim()));
          return [...cleaned, pat.referral];
        });
        if (didReferral) {
          setAutoSeedReasons((prev) => ({ ...prev, [`specialty:${pat.referral}`]: why }));
        }
      }

      // Apply the imaging chip. Same race-safe dedupe pattern.
      let didImaging = false;
      if (pat.imaging) {
        setImagingOrders((prev) => {
          const alreadyHas = (prev || []).some((i) =>
            i?.type === pat.imaging.type && (i?.bodyPart || '') === (pat.imaging.bodyPart || '')
          );
          if (alreadyHas) return prev;
          didImaging = true;
          const cleaned = (prev || []).filter((i) => i && (i.type || i.bodyPart || i.indication));
          return [...cleaned, { ...pat.imaging }];
        });
        if (didImaging) {
          setAutoSeedReasons((prev) => ({ ...prev, [`imaging:${pat.imaging.type}`]: why }));
        }
      }

      // Open relevant chip sections so the new chips are visible.
      if (didReferral && setShowReferralsSection) setShowReferralsSection(true);
      if (didImaging && setShowImagingSection) setShowImagingSection(true);

      // Surface the toast describing what got added.
      const toastMsg = reasonMatch ? (pat.reasonToast || pat.dictationToast) : pat.dictationToast;
      if (toastMsg && (didReferral || didImaging || pat.tip)) {
        toast.success(toastMsg, { duration: 3200 });
      }
      // Secondary "tip" toast for med suggestions (costochondritis NSAID,
      // MH SSRI). Kept separate so the chip toast and the tip toast
      // don't crowd each other.
      if (pat.tip) {
        toast.info(pat.tip, { duration: 5500 });
      }
      autoSeededRef.current.add(pat.id);
    });
  }, [reasonForVisit, notes, specialties, imagingOrders, setSpecialties, setImagingOrders, setShowReferralsSection, setShowImagingSection, AUTO_SEED_PATTERNS]);

  // ── Generation hook ──
  const effectiveLetterTone = effectiveRole === 'provider' ? providerLetterTone : letterTone;
  const generation = useGeneration({
    notes, setNotes, result, setResult, isGenerating, setIsGenerating,
    setDocumentsReady, setGenerationVersion, setLastShareUrl, setActiveOutputTab,
    generateIndication, generateClinicalNote, generateAdminLetter, generateLimitations,
    patientAge, patientGender, setPatientGender, patientStatus, chiefComplaint, patientLabel,
    soldierRank, soldierFirstName, soldierLastName, soldierDob, soldierUnit, soldierPhone,
    mtfName, customMtf, providerSignatureBlock, occupation, jobDuties,
    letterType, letterContext, charLimit, includeCoverLetter, setIncludeCoverLetter,
    includePatientQuotes, includeEmAnalysis,
    apFormat, clinicalTone,
    documentMode, messageScenario,
    encounterType, visitType, includeROS, includePE, peUnremarkable, peExceptions,
    medicMode,
    priorVisitContext,
    prepNotes,
    dosOverride,
    manualIcd10, liveIcd10Suggestions, primaryIcd10Codes,
    imagingOrders, specialties, effectiveLetterTone,
    labOrders, medsRxd, medsRxdDetail,
    selectedTemplate, shareCode, updateShareSession, autoShareTeamId,
    globalAutoStreamOn, userTeams,
    formatLockAp,
    currentClinicalNote: result?.clinical_note || '',
    user, guestId, effectiveRole,
    profileSignature, profileMTF, profileRank,
    letterheadLine1, letterheadLine2, letterheadAddress,
    showBracketModal, setShowBracketModal,
    bracketFields, setBracketFields,
    pendingGeneration, setPendingGeneration,
    showPreGenModal, setShowPreGenModal,
    preGenFields, setPreGenFields,
    setShowConflictModal, setConflicts,
    currentDraftId, saveDraft: formActions.saveDraft,
    loadHistory, trackFeature,
    setManualIcd10Order,
    language,
    deferredTopics,
    setDeferredTopics,
    // v6.8.47 — Saved injuries so useGeneration can fire the
    // "append to profile" nudge after disability letters.
    savedInjuries,
    onGenerationComplete: (data) => {
      // Auto-mark current draft as completed if it has a history_id
      if (currentDraftId) {
        markDraftsAsCompleted([currentDraftId]);
      }
    },
  });
  const { generateDocuments, handleIcd10Reorder, handleIcd10Delete,
          icd10PendingConfirm, confirmIcd10Sequence, dismissIcd10Confirm,
          handleBracketFieldsComplete, handlePreGenFieldsComplete, skipPreGenAndGenerate,
          proceedAfterConflict, dismissConflictModal, applyConflictFix,
          refreshAdminLetterOnly, refreshReferralOnly, scrubAndRefreshLetter, resetBypassConflict } = generation;

  // Quick Actions
  const { addQuickActionImaging, addQuickActionReferral, removeQuickActionImaging, removeQuickActionReferral,
          applyQuickActionImaging, applyQuickActionReferral } = useQuickActions({
    quickActions, setQuickActions, imagingOrders, setImagingOrders, specialties, setSpecialties,
  });

  React.useEffect(() => {
    // v6.8.47 — When useGeneration nudges the user to save current
    // dictation to their medical history, this listener opens the
    // Profile modal AND broadcasts the pre-fill so the
    // SavedInjuriesEditor can drop the first-line draft into its
    // label input.
    const handler = (e) => {
      try {
        // v6.8.47 — Route to the right profile modal based on mode:
        // patient mode uses PatientProfileSettings; everyone else uses
        // the generic ProfileModal.
        if (effectiveRole === 'patient') {
          setShowPatientProfile(true);
        } else {
          setShowProfileModal(true);
        }
        const draftLabel = e?.detail?.draftLabel || '';
        if (draftLabel) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('writeright:prefill-injury-draft', {
              detail: { draftLabel },
            }));
          }, 400);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('writeright:open-profile-with-injury-draft', handler);
    return () => window.removeEventListener('writeright:open-profile-with-injury-draft', handler);
  }, [effectiveRole]);

  // Feb 27, 2026 — Listen for history-loaded event so we can reset the
  // conflict-scan bypass flag in useGeneration. (loadHistoryItem lives in
  // useFormActions, which doesn't own the useGeneration ref.)
  React.useEffect(() => {
    const handler = () => {
      if (typeof resetBypassConflict === 'function') resetBypassConflict();
    };
    window.addEventListener('writeright:history-loaded', handler);
    return () => window.removeEventListener('writeright:history-loaded', handler);
  }, [resetBypassConflict]);

  // ── CRITICAL: localStorage autosave — prevents losing notes on crash/refresh ──
  React.useEffect(() => {
    if (!notes || notes.trim().length < 10) return;
    const t = setTimeout(() => {
      localStorage.setItem('writeright_autosave', JSON.stringify({ notes, savedAt: new Date().toISOString() }));
    }, 3000);
    return () => clearTimeout(t);
  }, [notes]);

  // ── EncounterErrorBoundary retry hook — when a scoped UI error tripped the
  // friendly fallback, the user's Retry button dispatches this event. We
  // re-run generation only if notes still exist (input survived).
  // Feb 27, 2026 — used to re-register the listener on every `notes` change
  // (causing addEventListener/removeEventListener churn on each keystroke).
  // Now we use a ref to read the latest notes/generateDocuments without
  // re-binding. Critical for typing performance.
  const notesRef = React.useRef(notes);
  const generateDocsRef = React.useRef(generateDocuments);
  React.useEffect(() => { notesRef.current = notes; }, [notes]);
  React.useEffect(() => { generateDocsRef.current = generateDocuments; }, [generateDocuments]);
  React.useEffect(() => {
    const onRetry = () => {
      try {
        const n = notesRef.current;
        const fn = generateDocsRef.current;
        if (n && n.trim().length > 0 && typeof fn === 'function') fn();
      } catch (err) { console.warn('retry-generate handler failed:', err); }
    };
    window.addEventListener('writeright:retry-generate', onRetry);
    return () => window.removeEventListener('writeright:retry-generate', onRetry);
  }, []);

  // beforeunload — save notes to localStorage if the user is closing the tab
  // with unsaved work. Same ref-based pattern so we don't churn the listener
  // on every keystroke (was a real source of typing lag).
  React.useEffect(() => {
    const handler = (e) => {
      const n = notesRef.current;
      if (n && n.trim().length >= 10) {
        localStorage.setItem('writeright_autosave', JSON.stringify({ notes: n, savedAt: new Date().toISOString() }));
        e.preventDefault(); e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Save as template wrapper
  const saveAsTemplate = () => saveAsTemplateHook(
    notes, letterContext, letterType, patientStatus, specialties, imagingOrders, setShowAuthModal
  );

  // Smart templates
  const { applySmartTemplate } = useSmartTemplates({
    user, effectiveRole,
    setIsLoadingSmartTemplates, setSmartTemplates, setMyProviders,
    setImagingOrders, setSpecialties, setPatientStatus,
  });

  // Profile side effects
  useEffect(() => {
    if (user) {
      if (user.signature_block) setProviderSignatureBlock(user.signature_block);
      if (user.default_mtf) setMtfName(user.default_mtf === 'custom' ? 'custom' : user.default_mtf);
      if (user.first_name) setSoldierFirstName(user.first_name);
      if (user.last_name) setSoldierLastName(user.last_name);
      if (user.date_of_birth) setSoldierDob(user.date_of_birth);
      if (user.phone) setSoldierPhone(user.phone);
      if (user.occupation) setOccupation(user.occupation);
      // Auto-derive patientName from first+last if patientName is empty.
      // Feb 24, 2026 fix: signed-in users were never getting their name
      // auto-populated into the wizard's `patientName` field, forcing
      // re-entry every refresh.
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      if (fullName && !patientName) setPatientName(fullName);

      // ALSO fetch /api/patient/profile for fields the auth user object
      // doesn't carry (rank, unit, job_duties, patient_status). Only fill
      // identity fields if they're currently empty (don't clobber what the
      // user typed manually).
      (async () => {
        try {
          const res = await axios.get(`${API}/patient/profile`, { withCredentials: true });
          const p = res.data || {};
          if (p.rank && !soldierRank) setSoldierRank(p.rank);
          if (p.unit && !soldierUnit) setSoldierUnit(p.unit);
          if (p.job_duties && !jobDuties) setJobDuties(p.job_duties);
          if (p.patient_status && !patientStatus) setPatientStatus(p.patient_status);
          if (p.phone && !soldierPhone) setSoldierPhone(p.phone);
          if (p.dob && !soldierDob) setSoldierDob(p.dob);
          if (p.occupation && !occupation) setOccupation(p.occupation);
          if (p.first_name && !soldierFirstName) setSoldierFirstName(p.first_name);
          if (p.last_name && !soldierLastName) setSoldierLastName(p.last_name);
          const pFull = `${p.first_name || ''} ${p.last_name || ''}`.trim();
          if (pFull && !patientName) setPatientName(pFull);
        } catch { /* not signed in / endpoint not available — silent */ }
      })();

      // ROS/PE defaults from profile — only apply if visit type is In-Person
      // Virtual toggle auto-manages PE, so profile should not override it
      const isVirtual = visitType === 'virtual' || visitType === 'results_review_virtual';
      const rosDefault = user.default_include_ros !== undefined ? user.default_include_ros : true;
      setIncludeROS(rosDefault);
      // Medic Mode (Feb 26, 2026): now hydrates from profile so the toggle
      // starts in the user's preferred state. Still overrideable per-encounter
      // via the input-card checkbox. Reset back to profile default on Next Pt.
      if (user.default_medic_mode !== undefined && typeof setMedicMode === 'function') {
        setMedicMode(!!user.default_medic_mode);
      }
      if (!isVirtual) {
        const peDefault = user.default_include_pe !== undefined ? user.default_include_pe : true;
        setIncludePE(peDefault);
      }
      if (user.default_mood) {
        const moodIndex = MOOD_THEMES.findIndex(m => m.id === user.default_mood);
        if (moodIndex >= 0) { setCurrentMoodIndex(moodIndex); localStorage.setItem('mediscribe_mood', user.default_mood); }
      }
      if (user.default_text_size !== undefined && user.default_text_size !== null) setTextSizeLevel(user.default_text_size);
      if (user.default_letter_tone) setProviderLetterTone(user.default_letter_tone);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save profile wrapper
  const saveProfile = async () => {
    await saveProfileToServer((saved) => {
      setIncludeROS(saved.defaultIncludeROS);
      // Only apply PE default if currently In-Person — Virtual toggle owns PE state
      const isVirtual = visitType === 'virtual' || visitType === 'results_review_virtual';
      if (!isVirtual) setIncludePE(saved.defaultIncludePE);
      setVisitType(saved.defaultVisitType);
      setInputMode(saved.defaultInputMode);
      setSoldierFirstName(saved.personalFirstName);
      setSoldierLastName(saved.personalLastName);
      setSoldierDob(saved.personalDOB);
      setSoldierPhone(saved.personalPhone);
      setOccupation(saved.personalOccupation);
    });
  };

  // Apply template
  const applyTemplate = (templateId) => {
    const userTemplate = userTemplates.find(t => t.id === templateId);
    if (userTemplate) {
      setNotes(userTemplate.notes_template || '');
      setLetterContext(userTemplate.context_template || '');
      if (userTemplate.letter_type) setLetterType(userTemplate.letter_type);
      if (userTemplate.patient_status) setPatientStatus(userTemplate.patient_status);
      if (userTemplate.specialties?.length) setSpecialties(userTemplate.specialties);
      if (userTemplate.imaging_types?.length) setImagingOrders(userTemplate.imaging_types);
      setGenerateAdminLetter(!!userTemplate.letter_type);
      setSelectedTemplate(templateId);
      toast.success(`Template "${userTemplate.name}" applied.`);
      return;
    }
    const template = templates.templates.find(t => t.id === templateId);
    if (template) {
      setNotes(template.notes_template);
      setLetterContext(template.context_template);
      setLetterType(template.letter_type);
      if (template.patient_status) setPatientStatus(template.patient_status);
      setGenerateAdminLetter(true);
      setSelectedTemplate(templateId);
      toast.success(`Template "${template.name}" applied. Fill in the [PLACEHOLDERS].`);
    }
  };

  // Load default provider/MTF from localStorage
  useEffect(() => {
    const savedProvider = localStorage.getItem('mediscribe_default_provider');
    if (savedProvider) setProviderSignatureBlock(savedProvider);
    const savedMtf = localStorage.getItem('mediscribe_default_mtf');
    if (savedMtf) {
      if (MTF_OPTIONS.some(m => m.value === savedMtf)) setMtfName(savedMtf);
      else { setMtfName('custom'); setCustomMtf(savedMtf); }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Post-generation clear effect
  useEffect(() => {
    if (!isGenerating && result && localStorage.getItem('writeright_clear_after_generate') === 'true') {
      localStorage.removeItem('writeright_clear_after_generate');
      setTimeout(() => { formActions.clearAll(true); toast.success('Saved to history! Ready for next patient.'); }, 1500);
    }
  }, [isGenerating, result]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDefaultVisitTypeChangeWithSync = (newType) => { handleDefaultVisitTypeChange(newType); setVisitType(newType); };

  // Note title
  const noteTitle = generateNoteTitle({
    result, chiefComplaint, reasonForVisit, customReasonForVisit,
    notes, visitType, patientLabel, history, REASON_FOR_VISIT_OPTIONS,
    dosOverride, dosTimeOverride,
  });
  const getLetterTypeLabel = (value) => {
    const type = LETTER_TYPES.find(lt => lt.value === value);
    return type ? type.label : value || 'N/A';
  };

  // CopyButton component — closure captures handleCopy/copiedField from parent
  const CopyButton = ({ text, field, label = "Copy" }) => (
    <Button variant="ghost" size="sm" onClick={() => handleCopy(text, field)}
      className={`h-8 px-3 text-muted-foreground hover:text-primary hover:bg-primary/10 ${copiedField === field ? 'copy-success' : ''}`}
      data-testid={`copy-${field}-button`} title={`Copy ${label.toLowerCase()}`}>
      {copiedField === field ? <Check className="w-4 h-4 mr-1 text-green-600" /> : <Copy className="w-4 h-4 mr-1" />}
      {copiedField === field ? 'Copied!' : label}
    </Button>
  );

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onGenerateDocs: generateDocuments,
    onCopyAll: () => {
      if (copyAllDocuments && result) {
        copyAllDocuments(result, noteTitle, manualIcd10, primaryIcd10Codes, fullSignatureBlock || providerSignatureBlock);
      }
    },
    onNextPatient: () => formActions.clearAll(true),
    onToggleLegend: () => setShowShortcutLegend(prev => !prev),
    isGenerating,
  });

  // Legal page navigation
  const handleLegalNav = useCallback((page) => {
    if (page === 'terms' || page === 'privacy' || page === 'hipaa' || page === 'compliance' || page === 'security-faq' || page === 'procurement') setLegalPage(page);
    else setLegalPage(null);
  }, []);

  // Quick Letter — from PatientTypeSelector, skip to Situation step
  const handleQuickLetter = useCallback((ql) => {
    // Set patient type
    formActions.selectPatientType(ql.patientType);
    // Set document mode & letter type
    setDocumentMode(ql.documentMode);
    if (ql.documentMode === 'message') {
      setMessageScenario(ql.messageScenario || '');
    } else {
      setLetterType(ql.letterType);
      setGenerateAdminLetter(true);
    }
    if (ql.isLimitations) setGenerateLimitations(true);
    // Start wizard at Step 2 (Situation)
    setInitialWizardStep(1);
  }, [formActions, setDocumentMode, setMessageScenario, setLetterType, setGenerateAdminLetter, setGenerateLimitations]);

  const scrollToHistoryCard = useCallback(() => {
    setShowHistory(true);
    setTimeout(() => {
      const historyCard = document.querySelector('[data-testid="history-card"]');
      if (historyCard) {
        historyCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
        historyCard.style.transition = 'box-shadow 0.3s';
        historyCard.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
        setTimeout(() => { historyCard.style.boxShadow = ''; }, 1500);
      }
    }, 300);
  }, []);

  // Legal pages
  const TermsPage = React.lazy(() => import('./pages/TermsPage'));
  const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
  const HipaaPage = React.lazy(() => import('./pages/HipaaPage'));
  const ComplianceOnePagerPage = React.lazy(() => import('./pages/ComplianceOnePagerPage'));
  const SecurityFaqPage = React.lazy(() => import('./pages/SecurityFaqPage'));
  const ProcurementPacketPage = React.lazy(() => import('./pages/ProcurementPacketPage'));
  if (legalPage === 'terms') return <React.Suspense fallback={<Loader2 className="w-8 h-8 animate-spin" />}><TermsPage onBack={(page) => page ? setLegalPage(page) : setLegalPage(null)} /></React.Suspense>;
  if (legalPage === 'privacy') return <React.Suspense fallback={<Loader2 className="w-8 h-8 animate-spin" />}><PrivacyPage onBack={(page) => page ? setLegalPage(page) : setLegalPage(null)} /></React.Suspense>;
  if (legalPage === 'hipaa') return <React.Suspense fallback={<Loader2 className="w-8 h-8 animate-spin" />}><HipaaPage onBack={(page) => page ? setLegalPage(page) : setLegalPage(null)} /></React.Suspense>;
  if (legalPage === 'compliance') return <React.Suspense fallback={<Loader2 className="w-8 h-8 animate-spin" />}><ComplianceOnePagerPage onBack={() => setLegalPage(null)} /></React.Suspense>;
  if (legalPage === 'security-faq') return <React.Suspense fallback={<Loader2 className="w-8 h-8 animate-spin" />}><SecurityFaqPage onBack={() => setLegalPage(null)} /></React.Suspense>;
  if (legalPage === 'procurement') return <React.Suspense fallback={<Loader2 className="w-8 h-8 animate-spin" />}><ProcurementPacketPage onBack={() => setLegalPage(null)} /></React.Suspense>;

  // ── Toggle section props (passed to ProviderInputCard) ──
  const toggleSectionProps = {
    includeROS, setIncludeROS, includePE, setIncludePE,
    peUnremarkable, setPeUnremarkable,
    peExceptions, setPeExceptions, markPeFindingsManual,
    imagingOrders, showImagingSection, setShowImagingSection,
    addImaging, removeImaging, updateImagingType, updateImagingBodyPart, updateImagingIndication, restoreImaging,
    setImagingOrders,
    specialties, showReferralsSection, setShowReferralsSection,
    addSpecialty, removeSpecialty, updateSpecialty, restoreSpecialty,
    setSpecialties,
    showLabsSection, setShowLabsSection,
    labOrders, setLabOrders,
    medsRxd, setMedsRxd,
    medsRxdDetail, setMedsRxdDetail,
    generateLimitations, setGenerateLimitations,
    generateAdminLetter, setGenerateAdminLetter,
    letterType, setLetterType,
    customLetterDescription, setCustomLetterDescription,
    includeEmAnalysis, setIncludeEmAnalysis,
    apFormat, setApFormat,
    charLimit, setCharLimit, showAdvanced, setShowAdvanced,
    quickActions,
    addQuickActionImaging, addQuickActionReferral,
    selectedRestriction, setSelectedRestriction,
    showRestrictionsHelper, setShowRestrictionsHelper,
    copyRestrictions: formActions.copyRestrictions,
    providerLetterTone, setProviderLetterTone,
    includePatientQuotes, setIncludePatientQuotes,
    clinicalTone, setClinicalTone,
    consultRecipient, setConsultRecipient,
    consultQuestion, setConsultQuestion,
    // v6.8.43 — auto-seed explainer map flows through to chip rows.
    autoSeedReasons,
    clearAutoSeedReason,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="mediscribe-app">
      <VersionChecker />
      <OfflineBanner />
      <OfflineQueuePanel open={showOfflineQueuePanel} onClose={() => setShowOfflineQueuePanel(false)} />
      {/* Mar 2, 2026 — KeyboardCheatsheet moved into <FloatingWidgetDock>
          alongside FloatingBugReportButton so they no longer stack in the
          bottom-right corner and steal clicks from the References footer. */}
      <IdlePrivacyLock />
      <Toaster
        position="top-center"
        expand={false}
        visibleToasts={1}
        duration={2500}
        closeButton
        offset={{ top: '72px' }}
        toastOptions={{
          className: 'z-[100000] !border-border !text-foreground !shadow-lg',
          style: { zIndex: 100000 },
        }}
        style={{ zIndex: 100000 }}
      />
      
      {/* Role Selector */}
      {!effectiveRole && (
        <RoleSelector onSelectRole={formActions.selectRole} onShowAuth={() => setShowAuthModal(true)}
          currentMood={currentMood} currentMoodIndex={currentMoodIndex} setCurrentMoodIndex={setCurrentMoodIndex}
          user={user} onLegalNavigate={handleLegalNav} />
      )}
      
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onSuccess={() => {}} onBeforeGoogleAuth={saveGuestSession} />
      
      {effectiveRole === 'patient' && !patientType && (
        <PatientTypeSelector onSelectType={formActions.selectPatientType}
          onBack={() => { setIsRoleSwitching(true); setUserRole(null); }}
          onQuickLetter={handleQuickLetter} />
      )}
      
      {effectiveRole && (effectiveRole === 'provider' || patientType) ? (
      <>
      <NetworkDraftBanner onRestore={(d) => { setNotes(d.notes); toast.success('Notes restored into editor'); }} />
      <WhiteSpaceBanner
        history={historyHook.history}
        effectiveRole={effectiveRole}
        onShowNeedsSubmit={() => {
          setHistoryFilter('unsubmitted');
          setShowHistorySlidePanel(true);
        }}
      />
      <Header
        textSizeLevel={textSizeLevel} setTextSizeLevel={setTextSizeLevel}
        currentMood={currentMood} currentMoodIndex={currentMoodIndex} setCurrentMoodIndex={setCurrentMoodIndex}
        favoriteMoods={favoriteMoods} setFavoriteMoods={setFavoriteMoods}
        showMoodInfo={showMoodInfo} setShowMoodInfo={setShowMoodInfo}
        shareCode={shareCode} setShowShareModal={setShowShareModal}
        startShareSession={startShareSession} startShareHistory={startShareHistory}
        historyShareCode={historyShareCode} isSharing={isSharing}
        user={user} setShowAuthModal={setShowAuthModal} setShowProfileModal={setShowProfileModal}
        setShowTeamSettings={setShowTeamSettings} setShowProductivityReport={setShowProductivityReport} myProviders={myProviders} logout={logout}
        userRole={effectiveRole} switchRole={formActions.switchRole}
        forceSingleColumn={forceSingleColumn} toggleColumnLayout={toggleColumnLayout}
        readingWidth={readingWidth} setReadingWidth={setReadingWidth}
        readyForSignature={getReadyForSignature()} onShowSignatureCenter={scrollToHistoryCard}
        pendingDraftsCount={getPendingDraftsCount()} onShowDrafts={scrollToHistoryCard}
        onShowAllHistory={() => { setShowHistorySlidePanel(true); trackFeature('history_panel'); }}
        onShowTutorial={() => setShowTutorialSlideshow(effectiveRole || 'provider')}
        onShowTeamCollab={() => setShowTeamBoardPanel(true)}
        teamUnreadCount={teamUnreadCount} autoShareActive={!!autoShareTeamId}
        hasTeam={Boolean(user?.team_id)}
        onShowBatchCharting={() => { setShowBatchCharting(true); trackFeature('speed_chart'); }}
        onShowBugReport={() => setShowBugReport(true)}
        onShowShortcuts={() => setShowShortcutLegend(true)}
        onShowShipVersion={() => setShowShipVersion(true)}
        includePatientQuotes={includePatientQuotes}
        setIncludePatientQuotes={setIncludePatientQuotes}
        onNextPatient={formActions.nextPatient}
        onGenerateDocs={generateDocuments}
        isGenerating={isGenerating}
        notes={notes}
        onClearAll={() => {
          // Mar 2, 2026 — Provider reported accidentally hitting Clear
          // instead of Update (both used the RefreshCw icon and sat close
          // together on narrow screens). Added confirmation to prevent
          // accidental data loss. Also changed the Clear icon to Eraser
          // and the Update icon to DownloadCloud so they're visually
          // unmistakable now. If the user has NO note content to lose,
          // we skip the confirm to keep the "next patient" workflow fast.
          const hasContent = (notes && notes.trim().length > 20)
            || (result && (result.clinical_note || result.admin_letter));
          if (!hasContent) {
            formActions.clearAll();
            return;
          }
          if (window.confirm('Clear all fields? This wipes your current draft (dictation, generated note, ICD-10s). This cannot be undone.')) {
            formActions.clearAll();
          }
        }}
        onCopyAll={() => {
          if (copyAllDocuments && result) {
            copyAllDocuments(result, noteTitle, manualIcd10, primaryIcd10Codes, fullSignatureBlock || providerSignatureBlock);
          }
        }}
        copyAllReady={!!(result?.clinical_note || result?.admin_letter || result?.portal_message)}
        onCopyNoteOnly={() => {
          if (copyClinicalNoteOnly && result) {
            copyClinicalNoteOnly(result, noteTitle, fullSignatureBlock || providerSignatureBlock);
          }
        }}
        copyNoteReady={!!result?.clinical_note}
        onCopyOrdersOnly={() => {
          if (copyOrdersOnly && result) {
            copyOrdersOnly(result, manualIcd10, primaryIcd10Codes);
          }
        }}
        copyOrdersReady={!!(result?.referrals?.length || result?.imaging_orders?.length || result?.icd10_suggestions?.length || manualIcd10)}
        // Feb 27, 2026 — Copy NOTE TITLE only (for trackers/handoff lists).
        onCopyTitle={() => {
          if (noteTitle) {
            navigator.clipboard.writeText(noteTitle).then(() => {
              toast.success('Title copied', { duration: 1500 });
            }).catch(() => toast.error('Copy failed'));
          } else {
            toast.error('No note title yet — generate first');
          }
        }}
        copyTitleReady={!!noteTitle}
        onSaveDraft={() => formActions.saveDraft && formActions.saveDraft()}
        saveDraftReady={!!(notes?.trim() || result?.clinical_note || result?.admin_letter || result?.portal_message)}
        onSignAndCopy={() => {
          // Top-bar combo: fire copy-all THEN dispatch the same "note-submitted"
          // event the stoplight combo uses so the encounter promotes to green.
          // Feb 24, 2026: provider explicitly asked for this shortcut to live
          // on the top toolbar.
          try {
            if (copyAllDocuments && result) {
              copyAllDocuments(result, noteTitle, manualIcd10, primaryIcd10Codes, fullSignatureBlock || providerSignatureBlock);
            }
          } catch (e) { console.warn('[SignCopyTop] copy failed:', e?.message); }
          try {
            const hid = result?.history_id || result?.id;
            if (hid && updateHistoryItem) {
              updateHistoryItem(hid, { completed: true }, { silent: true });
            }
            const encId = hid || (result?.clinical_note ? `enc-${(result.clinical_note || '').length}-${(result.original_notes || '').length}` : null);
            if (encId) {
              window.dispatchEvent(new CustomEvent('writeright:note-submitted', { detail: { encounter_id: encId } }));
            }
            if (loadHistory) loadHistory();
          } catch (e) { console.warn('[SignCopyTop] sign failed:', e?.message); }
        }}
        signAndCopyReady={!!(result?.clinical_note || result?.admin_letter || result?.portal_message)}
        onShowOfflineQueue={() => setShowOfflineQueuePanel(true)}
        // Feb 22, 2026 — header stoplight indicator
        currentEncounterId={
          result?.history_id || result?.id ||
          (result?.clinical_note ? `enc-${(result.clinical_note || '').length}-${(result.original_notes || '').length}` : null)
        }
        onHeaderSignedToggle={async (checked) => {
          const hid = result?.history_id || result?.id;
          if (!hid || typeof hid !== 'string' || hid.startsWith('enc-')) return;
          try {
            if (updateHistoryItem) {
              await updateHistoryItem(hid, { completed: !!checked }, { silent: true });
            }
            if (loadHistory) loadHistory();
          } catch (e) {
            console.warn('[StoplightHeader] backend sync failed:', e?.message);
          }
        }}
      />
      
      {/* Team Message Bar — persistent above dashboard */}
      {user?.team_id && (
        <div className="fixed left-0 right-0 z-40" style={{ top: '60px' }}>
          <TeamMessageBar user={user} teamUnreadCount={teamUnreadCount} setTeamUnreadCount={setTeamUnreadCount} />
        </div>
      )}

      {/* One-time v4.7 update reminder */}
      {showUpdateReminder && effectiveRole === 'provider' && (
        <div className="mx-auto max-w-5xl px-4 mt-2 mb-1" data-testid="update-reminder-banner">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5">
            <div className="flex items-center gap-2 text-xs text-foreground">
              <span className="text-amber-400 font-bold">v{APP_VERSION}</span>
              <span className="text-muted-foreground">|</span>
              <span>Visit Prep Script · Submitted to Genesis · Network Resilience · Signal Safety Net · NSI Surfaced</span>
              <span className="text-muted-foreground">—</span>
              <span className="text-amber-400/80">Click <strong>Update</strong> in the toolbar to load the latest version</span>
            </div>
            <button onClick={() => { setShowUpdateReminder(false); localStorage.setItem('writeright_seen_version', APP_VERSION); }}
              className="shrink-0 text-foreground hover:text-amber-400 text-sm px-2 py-1 rounded hover:bg-secondary/50 transition-colors font-bold"
              data-testid="dismiss-update-reminder">
              X
            </button>
          </div>
        </div>
      )}

      <PatientProfileSettings open={showPatientProfile} onClose={() => setShowPatientProfile(false)} user={user}
        setSoldierFirstName={setSoldierFirstName} setSoldierLastName={setSoldierLastName}
        setSoldierDob={setSoldierDob} setSoldierRank={setSoldierRank} setSoldierUnit={setSoldierUnit}
        setOccupation={setOccupation} setJobDuties={setJobDuties}
        setSoldierPhone={setSoldierPhone} setPatientStatus={setPatientStatus} />

      {/* Feb 28, 2026 — Provider Productivity Report (OER-ready printable
          per-user stats with date-range, PDF / Print / CSV exports).
          Opened from Header user menu → "Productivity Report". */}
      {showProductivityReport && (
        <ProviderProductivityReport onClose={() => setShowProductivityReport(false)} />
      )}

      <AppModals
        shareCode={shareCode} showShareModal={showShareModal} setShowShareModal={setShowShareModal}
        endShareSession={endShareSession} effectiveRole={effectiveRole}
        historyShareCode={historyShareCode} startShareHistory={startShareHistory} startShareSession={startShareSession}
        showBugReport={showBugReport} setShowBugReport={setShowBugReport} guestId={guestId}
        showShipVersion={showShipVersion} setShowShipVersion={setShowShipVersion}
        showMoodInfo={showMoodInfo} setShowMoodInfo={setShowMoodInfo}
        showSaveTemplateModal={showSaveTemplateModal} setShowSaveTemplateModal={setShowSaveTemplateModal}
        newTemplateName={newTemplateName} setNewTemplateName={setNewTemplateName}
        newTemplateDesc={newTemplateDesc} setNewTemplateDesc={setNewTemplateDesc}
        notes={notes} letterType={letterType} specialties={specialties} imagingOrders={imagingOrders}
        saveAsTemplate={saveAsTemplate}
        showProfileModal={showProfileModal} user={user} setShowProfileModal={setShowProfileModal}
        profileSignature={profileSignature} setProfileSignature={setProfileSignature}
        profileMTF={profileMTF} setProfileMTF={setProfileMTF}
        profileRank={profileRank} setProfileRank={setProfileRank}
        defaultMood={defaultMood} setDefaultMood={setDefaultMood}
        defaultTextSize={defaultTextSize} setDefaultTextSize={setDefaultTextSize}
        defaultIncludeROS={defaultIncludeROS} setDefaultIncludeROS={setDefaultIncludeROS}
        defaultIncludePE={defaultIncludePE} setDefaultIncludePE={setDefaultIncludePE}
        defaultMedicMode={defaultMedicMode} setDefaultMedicMode={setDefaultMedicMode}
        defaultVisitType={defaultVisitType} setDefaultVisitType={setDefaultVisitType}
        visitSchedule={visitSchedule} setVisitSchedule={setVisitSchedule}
        defaultInputMode={defaultInputMode} setDefaultInputMode={setDefaultInputMode}
        personalFirstName={personalFirstName} setPersonalFirstName={setPersonalFirstName}
        personalLastName={personalLastName} setPersonalLastName={setPersonalLastName}
        personalDOB={personalDOB} setPersonalDOB={setPersonalDOB}
        personalPhone={personalPhone} setPersonalPhone={setPersonalPhone}
        personalOccupation={personalOccupation} setPersonalOccupation={setPersonalOccupation}
        savedInjuries={savedInjuries} setSavedInjuries={setSavedInjuries}
        defaultLetterTone={defaultLetterTone} setDefaultLetterTone={setDefaultLetterTone}
        letterheadLine1={letterheadLine1} setLetterheadLine1={setLetterheadLine1}
        letterheadLine2={letterheadLine2} setLetterheadLine2={setLetterheadLine2}
        letterheadAddress={letterheadAddress} setLetterheadAddress={setLetterheadAddress}
        handleDefaultVisitTypeChangeWithSync={handleDefaultVisitTypeChangeWithSync}
        handleDefaultInputModeChange={handleDefaultInputModeChange}
        providerLetterTone={defaultLetterTone}
        saveProfile={saveProfile} isSavingProfile={isSavingProfile}
        clinicalTone={clinicalTone} setClinicalTone={setClinicalTone}
        showTeamSettings={showTeamSettings} setShowTeamSettings={setShowTeamSettings}
        showHistorySlidePanel={showHistorySlidePanel} setShowHistorySlidePanel={setShowHistorySlidePanel}
        history={history} loadHistoryItem={formActions.loadHistoryItem} deleteHistoryItem={deleteHistoryItem} loadHistory={loadHistory}
        historyFilter={historyFilter} setHistoryFilter={setHistoryFilter}
        historySearchQuery={historySearchQuery} setHistorySearchQuery={setHistorySearchQuery}
        dateJump={dateJump} setDateJump={setDateJump}
        sortOrder={sortOrder} setSortOrder={setSortOrder}
        batchSelectedItems={batchSelectedItems} toggleBatchSelect={toggleBatchSelect}
        batchDeleteItems={batchDeleteItems} markDraftsAsCompleted={markDraftsAsCompleted} updateHistoryItem={updateHistoryItem}
        textSizeLevel={textSizeLevel}
        showTeamBoardPanel={showTeamBoardPanel} setShowTeamBoardPanel={setShowTeamBoardPanel}
        autoShareTeamId={autoShareTeamId} autoShareDrafts={autoShareDrafts}
        toggleAutoShareTeam={toggleAutoShareTeam} toggleAutoShareDrafts={toggleAutoShareDrafts}
        globalAutoStreamOn={globalAutoStreamOn}
        setTeamUnreadCount={setTeamUnreadCount}
        onLoadTeamNote={(note) => {
          // Feb 27, 2026 — Pick Up Baton must restore FULL payload, not just
          // demographics. Previously this handler only set notes + chief
          // complaint + patient_label — title and ICD-10 codes silently
          // dropped. Now we route through `loadHistoryItem` (the same path
          // History uses) so EVERY field the registry knows about — chief
          // complaint, age, gender, status, ICD-10 suggestions, imaging,
          // referrals/specialties, deferred topics, PE exceptions — comes
          // through. We then explicitly clear `result` so the previous
          // patient's generated documents do NOT bleed into the new
          // encounter (clinical_note from the shared note is the teammate's
          // draft, not ours).
          const teamNoteAsHistoryItem = {
            ...note,
            // Team-shared notes use field `notes`; history registry reads from `original_notes`.
            original_notes: note.notes || note.original_notes || '',
          };
          formActions.loadHistoryItem(teamNoteAsHistoryItem);
          // Clear output so the teammate's clinical_note doesn't carry as if it were ours.
          setResult(null);
          // Un-dead Generate: registry already forces generateClinicalNote=true via
          // loadHistoryItem; also reset the conflict-scan bypass flag so a stale
          // "Generate Anyway" choice on a prior patient doesn't silently skip
          // scanning the newly-loaded text.
          if (typeof resetBypassConflict === 'function') resetBypassConflict();
          setShowTeamBoardPanel(false);
          // Stamp the pickup pill so the provider sees who/when this came from.
          // Feb 27, 2026 — also stash team_id + team_name so the "Hand back"
          // button on the pill can re-share the (updated) draft back to the
          // same team without forcing the provider to re-open Team Board.
          setPickupMeta({
            from: note.shared_by_name || note.author_name || 'teammate',
            at: note.shared_at || note.updated_at || new Date().toISOString(),
            patient_label: note.patient_label || null,
            team_id: note.team_id || null,
            team_name: note.team_name || null,
          });
          toast.success('Baton picked up — input + ICD-10 + demographics restored. Output cleared, ready to regenerate.');
        }}
        showTutorialSlideshow={showTutorialSlideshow} setShowTutorialSlideshow={setShowTutorialSlideshow}
        showTeamCollab={showTeamCollab} setShowTeamCollab={setShowTeamCollab}
        onLoadCollabNote={(note) => {
          // Feb 27, 2026 — Pickup baton from collab panel: full payload
          // restore via the same `loadHistoryItem` path as team pickup.
          // Previously only notes + chief_complaint + patient demographics
          // were restored; ICD-10 / imaging / referrals dropped.
          const teamNoteAsHistoryItem = {
            ...note,
            original_notes: note.notes || note.original_notes || '',
          };
          formActions.loadHistoryItem(teamNoteAsHistoryItem);
          setResult(null);
          if (typeof resetBypassConflict === 'function') resetBypassConflict();
          setShowTeamCollab(false);
          setPickupMeta({
            from: note.author_name || note.shared_by_name || 'teammate',
            at: note.shared_at || note.updated_at || new Date().toISOString(),
            patient_label: note.patient_label || null,
            team_id: note.team_id || null,
            team_name: note.team_name || null,
          });
          toast.success(`Baton received from ${note.author_name || 'teammate'} — full payload restored, ready to regenerate.`);
        }}
        patientLabel={patientLabel}
        showShortcutLegend={showShortcutLegend} setShowShortcutLegend={setShowShortcutLegend}
        showRestoreDialog={showRestoreDialog} handleRestoreChoice={handleRestoreChoice} pendingRestoreData={pendingRestoreData}
        showBracketModal={showBracketModal} setShowBracketModal={setShowBracketModal}
        bracketFields={bracketFields} setBracketFields={setBracketFields}
        setPendingGeneration={setPendingGeneration} handleBracketFieldsComplete={handleBracketFieldsComplete}
        showPreGenModal={showPreGenModal} setShowPreGenModal={setShowPreGenModal}
        preGenFields={preGenFields} setPreGenFields={setPreGenFields}
        handlePreGenFieldsComplete={handlePreGenFieldsComplete} skipPreGenAndGenerate={skipPreGenAndGenerate}
        showConflictModal={showConflictModal} conflicts={conflicts}
        proceedAfterConflict={proceedAfterConflict} dismissConflictModal={dismissConflictModal}
        applyConflictFix={applyConflictFix}
      />

      {showBatchCharting ? (
        <div className="w-full flex flex-col" style={{ height: '100vh', paddingTop: '92px' }}>
          <BatchChartingGrid user={user} guestId={guestId} onClose={() => setShowBatchCharting(false)} history={history} clinicalTone={clinicalTone} />
        </div>
      ) : (
      <main className={`w-full py-3 lg:py-4 pb-20 sm:pb-4 pt-36 sm:pt-32 lg:pt-[140px] ${textSizeClass} ${
        forceSingleColumn ? 'px-2 sm:px-3 lg:px-4 xl:px-6' : 'px-3 sm:px-5 lg:px-8 xl:px-12'
      } ${effectiveRole === 'provider' && !forceSingleColumn ? 'sm:pb-4' : 'min-h-[calc(100vh-120px)]'}`}>
        {/* Patient Mode Welcome */}
        {effectiveRole === 'patient' && (
          <div className={`mb-4 p-3 ${patientType === 'va_claims' ? 'bg-green-600/10 border-green-600/20' : 'bg-primary/10 border-primary/20'} border rounded-lg flex items-start justify-between gap-3`}>
            <p className="text-sm text-foreground">
              {patientType === 'va_claims' ? (
                <>
                  <strong>You've earned this.</strong> This tool helps you organize your service history, medical information, and personal experiences
                  into clear documents for your VA claim. It does not provide legal or medical advice — it helps you articulate what happened and why it matters.
                </>
              ) : (
                <>
                  <strong>Welcome.</strong> This tool helps you organize your medical information into a draft letter or portal message for your provider. 
                  It does not replace medical decision-making—it simply helps you participate in your care and makes it easier for your provider to advocate for you.
                </>
              )}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              {/* v6.8.46 — Back to "What best describes you?" button.
                  Previously va_claims persona was a dead-end (no way to
                  switch persona without resetting the whole session). */}
              {patientType && (
                <Button variant="outline" size="sm"
                  onClick={() => {
                    try { localStorage.removeItem('writeright_patient_type'); } catch { /* ignore */ }
                    setPatientType(null);
                    if (letterType) setLetterType('');
                    setInitialWizardStep(0);
                  }}
                  className="gap-1.5 text-xs"
                  data-testid="back-to-persona-btn"
                  title="Switch persona"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /><span className="hidden sm:inline">Change Persona</span>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowPatientProfile(true)}
                className="gap-1.5 text-xs" data-testid="patient-profile-btn">
                <Settings className="w-3.5 h-3.5" /><span className="hidden sm:inline">My Profile</span>
              </Button>
            </div>
          </div>
        )}
        
        {forceSingleColumn && isLargeScreen && effectiveRole === 'provider' && (
          <SectionNav activeSection="input-section" hasOutput={!!result} />
        )}
        
        {/* ═══ PATIENT MODE ═══ */}
        {effectiveRole === 'patient' && (
          <div className="w-full space-y-6">
            <div id="input-section">
              {patientType === 'va_claims' ? (
                <VAClaimsWizard
                  onInsertText={(text) => setNotes(prev => prev + text)}
                  notes={notes}
                  setNotes={setNotes}
                  generateDocuments={generateDocuments}
                  isGenerating={isGenerating}
                  result={result}
                  setGenerateAdminLetter={setGenerateAdminLetter}
                  setLetterType={setLetterType}
                  setDocumentMode={setDocumentMode}
                  setMessageScenario={setMessageScenario}
                  setLetterContext={setLetterContext}
                />
              ) : (
                <PatientWizard
                DemographicsComponent={PatientDemographics}
                demographicsProps={{
                  soldierFirstName, setSoldierFirstName, soldierLastName, setSoldierLastName,
                  soldierDob, setSoldierDob, patientStatus, setPatientStatus,
                  soldierRank, setSoldierRank, soldierUnit, setSoldierUnit,
                  occupation, setOccupation, jobDuties, setJobDuties,
                }}
                NotesComponent={NotesInput}
                notesProps={{ effectiveRole, notes, setNotes, recording, notesHistory, undoNotes, onGenerateDocs: generateDocuments, isGenerating }}
                LimitationsComponent={PatientLimitationsHelper}
                limitationsProps={{ generateLimitations, setGenerateLimitations, selectedRestriction, setSelectedRestriction, setNotes }}
                LetterOptionsComponent={PatientLetterOptions}
                letterOptionsProps={{
                  letterType, setLetterType, setGenerateAdminLetter,
                  letterTone, setLetterTone, letterPageLimit, setLetterPageLimit,
                  includeCoverLetter, setIncludeCoverLetter,
                  letterFormat, setLetterFormat, referencePosition, setReferencePosition,
                  documentMode, setDocumentMode, messageScenario, setMessageScenario,
                }}
                ActionButtonsComponent={ActionButtons}
                actionButtonsProps={{ effectiveRole, isGenerating, notes, generateDocuments, clearAll: formActions.clearAll, priorVisitContext, prepNotes }}
                notes={notes} soldierFirstName={soldierFirstName} soldierLastName={soldierLastName}
                patientStatus={patientStatus} letterType={letterType} effectiveRole={effectiveRole}
                language={language} setLanguage={setLanguage}
                documentMode={documentMode} messageScenario={messageScenario}
                initialStep={initialWizardStep}
                onChangePersona={() => {
                  // Feb 28, 2026 (v6.8.42) — Lets the user jump back to
                  // "What best describes you?" from any wizard step
                  // without dismissing the wizard entirely. We clear
                  // patientType so the PatientTypeSelector mounts; we
                  // also clear the in-progress letter type / wizard
                  // initial step so the user starts cleanly. Notes &
                  // dictation are preserved so nothing they typed is
                  // lost.
                  try { localStorage.removeItem('writeright_patient_type'); } catch { /* ignore */ }
                  setPatientType(null);
                  if (letterType) setLetterType('');
                  setInitialWizardStep(0);
                }}
              />
              )}
            </div>
            <div id="patient-output-section">
              <EncounterErrorBoundary>
                <OutputDocumentsPanel
                  result={result} effectiveRole={effectiveRole}
                  activeOutputTab={activeOutputTab} setActiveOutputTab={setActiveOutputTab}
                  isGenerating={isGenerating} copiedField={copiedField} handleCopy={handleCopy}
                  exportToWord={exportToWord} exportToPDF={exportToPDF}
                  shareLetter={shareLetter} getFilledLetter={getFilledLetter}
                  fillLetterPlaceholders={fillLetterPlaceholders}
                  noteTitle={noteTitle} manualIcd10={manualIcd10} primaryIcd10Codes={primaryIcd10Codes}
                  autoDetectedCodes={autoDetectedCodes}
                  patientAge={patientAge} patientGender={patientGender}
                  CopyButton={CopyButton}
                  copyAllDocuments={() => copyAllDocuments(result, noteTitle, manualIcd10, primaryIcd10Codes, providerSignatureBlock)}
                  notes={notes} soldierFirstName={soldierFirstName} letterType={letterType}
                  generateDocuments={generateDocuments}
                  refreshAdminLetterOnly={refreshAdminLetterOnly}
                  scrubAndRefreshLetter={scrubAndRefreshLetter}
                  setResult={setResult}
                  setExternalFullSignatureBlock={setFullSignatureBlock}
                  loadHistory={loadHistory}
                  updateHistoryItem={updateHistoryItem}
                />
              </EncounterErrorBoundary>
            </div>
          </div>
        )}
        
        {/* ═══ PROVIDER MODE ═══ */}
        {effectiveRole === 'provider' && (
        <>
        {/* Feb 27, 2026 — Patient Setup Header. Wide bar above the 2-col
            grid hosting Quick Demographics + Reason-for-Visit + Chief
            Complaint. Pre-Visit Prep, Visit Details, Smart Context Carry
            remain BELOW in the input column. Sticky on lg: desktop. */}
        <PatientSetupHeader
          patientAge={patientAge}
          setPatientAge={setPatientAge}
          patientGender={patientGender}
          setPatientGender={setPatientGender}
          patientStatus={patientStatus}
          setPatientStatus={setPatientStatus}
          medicMode={medicMode}
          setMedicMode={setMedicMode}
          dosOverride={dosOverride}
          setDosOverride={setDosOverride}
          dosTimeOverride={dosTimeOverride}
          setDosTimeOverride={setDosTimeOverride}
          lateEntry={lateEntry}
          setLateEntry={setLateEntry}
          virtualModality={virtualModality}
          setVirtualModality={setVirtualModality}
          visitType={visitType}
          setVisitType={setVisitType}
          appointmentType={appointmentType}
          setAppointmentType={setAppointmentType}
          reasonForVisit={reasonForVisit}
          setReasonForVisit={setReasonForVisit}
          sortedReasonOptions={sortedReasonOptions}
          customReasonForVisit={customReasonForVisit}
          setCustomReasonForVisit={setCustomReasonForVisit}
          chiefComplaint={chiefComplaint}
          setChiefComplaint={setChiefComplaint}
          autoFilledFields={autoFilledFields}
          setAutoFilledFields={setAutoFilledFields}
        />
        {/* Feb 27, 2026 (v6.8.14) — Row 3 redesigned per provider feedback:
            ROS removed (always implicit), PE always on (only "Except for…"
            sub-toggle controls remarkable findings), and a visual divider
            separates the Note section from Separate Documents (Imaging /
            Meds / Labs / Referrals / Profile / Letter / E/M). */}
        <OutputsHeaderRow
          peUnremarkable={peUnremarkable} setPeUnremarkable={setPeUnremarkable}
          peExceptions={peExceptions} setPeExceptions={setPeExceptions}
          markPeFindingsManual={markPeFindingsManual}
          imagingOrders={imagingOrders} showImagingSection={showImagingSection} setShowImagingSection={setShowImagingSection}
          addImaging={addImaging} setImagingOrders={setImagingOrders}
          medsRxd={medsRxd} setMedsRxd={setMedsRxd}
          medsRxdDetail={medsRxdDetail} setMedsRxdDetail={setMedsRxdDetail}
          showLabsSection={showLabsSection} setShowLabsSection={setShowLabsSection} setLabOrders={setLabOrders}
          specialties={specialties} showReferralsSection={showReferralsSection} setShowReferralsSection={setShowReferralsSection}
          addSpecialty={addSpecialty} setSpecialties={setSpecialties}
          generateLimitations={generateLimitations} setGenerateLimitations={setGenerateLimitations}
          generateAdminLetter={generateAdminLetter} setGenerateAdminLetter={setGenerateAdminLetter}
          letterType={letterType} setLetterType={setLetterType} setCustomLetterDescription={setCustomLetterDescription}
          includeEmAnalysis={includeEmAnalysis} setIncludeEmAnalysis={setIncludeEmAnalysis}
          manualIcd10={manualIcd10} setManualIcd10={setManualIcd10}
        />
        <div className={`grid gap-4 lg:gap-6 relative ${forceSingleColumn ? 'force-single-column mx-auto' : 'grid-cols-1 sm:grid-cols-2'}`}
          style={forceSingleColumn ? {
            gridTemplateColumns: '1fr',
            // Mar 2, 2026 — readingWidthPx sourced from useUIPreferences,
            // which honors the user's Narrow/Comfortable/Wide preference
            // AND scales with textSizeLevel. Provider approved a 3-way
            // toggle rather than a fixed value so folks can pick between
            // reading focus (narrow) and max screen coverage (wide).
            maxWidth: `${readingWidthPx}px`,
          } : {}}>

          {/* Floating Generate FAB — Mar 2, 2026: provider 3rd request — must FOLLOW
              the scroll (stay visible) while remaining CENTERED between the two
              columns (NOT bottom-right). Switched from `absolute top-32` (which
              anchored to the grid and scrolled OUT of view once the user paged
              past 128px) to `fixed top-[200px]` so the button stays pinned to
              the viewport at top-center as the provider scrolls through long
              notes. `left-1/2 -translate-x-1/2` keeps it horizontally centered
              over the column gap. */}
          {!forceSingleColumn && (
            <button
              type="button"
              onClick={() => { if (typeof generateDocuments === 'function') generateDocuments(); }}
              disabled={isGenerating || !notes?.trim()}
              className={`hidden lg:flex fixed left-1/2 top-[200px] -translate-x-1/2 z-30 items-center justify-center w-14 h-14 border-4 transition-all ${
                isGenerating
                  ? 'bg-amber-700 border-amber-300 text-amber-100 cursor-wait'
                  : (notes?.trim()
                    ? 'bg-emerald-600 border-emerald-300 text-white shadow-lg shadow-emerald-700/40 hover:bg-emerald-500 hover:scale-110 active:scale-95'
                    : 'bg-slate-700 border-slate-500 text-slate-400 cursor-not-allowed')
              }`}
              data-testid="floating-generate-btn"
              title={isGenerating ? 'Generating…' : (notes?.trim() ? 'Generate (or click anywhere with Ctrl+Enter)' : 'Type or dictate notes first')}
            >
              {isGenerating
                ? <Loader2 className="w-6 h-6 animate-spin" />
                : <img src="/emblem_icon.png" alt="" className="w-9 h-9 object-contain pointer-events-none" />}
            </button>
          )}

          <div id="input-section" className={`space-y-4 ${textSizeLevel > 0 ? 'space-y-6' : ''}`}>
            <BackChartingResumeBanner
              history={history}
              onOpenHistory={() => setShowHistorySlidePanel(true)}
            />
            <PickUpWhereYouLeftOff notes={notes} setNotes={setNotes} />
            <EncounterErrorBoundary>
              <ProviderInputCard
                setupInHeader={true}
                notes={notes} setNotes={setNotes} recording={recording}
                notesHistory={notesHistory} undoNotes={undoNotes}
                textSizeLevel={textSizeLevel} forceSingleColumn={forceSingleColumn}
                generateDocuments={generateDocuments} isGenerating={isGenerating}
                user={user}
                clearAll={formActions.clearAll} saveDraft={formActions.saveDraft} nextPatient={formActions.nextPatient}
                effectiveRole={effectiveRole}
                PatientLabelSection={PatientLabelSection} patientLabel={patientLabel} setPatientLabel={setPatientLabel}
                clinicSchedule={clinicSchedule} setClinicSchedule={setClinicSchedule}
                ProviderDemographics={ProviderDemographics}
                patientAge={patientAge} setPatientAge={setPatientAge}
                patientGender={patientGender} setPatientGender={setPatientGender}
                encounterType={encounterType} setEncounterType={setEncounterType}
                visitType={visitType} setVisitType={setVisitType}
                panelOwnership={panelOwnership} setPanelOwnership={setPanelOwnership}
                appointmentType={appointmentType} setAppointmentType={setAppointmentType}
                patientStatus={patientStatus} setPatientStatus={setPatientStatus}
                autoFilledFields={autoFilledFields} setAutoFilledFields={setAutoFilledFields}
                setIncludePE={setIncludePE} setPeUnremarkable={setPeUnremarkable}
                virtualModality={virtualModality} setVirtualModality={setVirtualModality}
                deferredTopics={deferredTopics} setDeferredTopics={setDeferredTopics}
                reasonForVisit={reasonForVisit} reasonAutoDetected={reasonAutoDetected}
                sortedReasonOptions={sortedReasonOptions}
                setReasonManually={setReasonManually} customReasonForVisit={customReasonForVisit}
                setCustomReasonManually={setCustomReasonManually}
                ChiefComplaintSection={ChiefComplaintSection}
                chiefComplaint={chiefComplaint} setChiefComplaint={setChiefComplaint}
                NotesInput={NotesInput}
                ProviderToggleSection={ProviderToggleSection} toggleSectionProps={toggleSectionProps}
                ActionButtons={ActionButtons}
                DetectedICD10Inline={DetectedICD10Inline}
                autoDetectedCodes={autoDetectedCodes}
                manualIcd10={manualIcd10}
                setManualIcd10={setManualIcd10}
                pickupMeta={pickupMeta} setPickupMeta={setPickupMeta}
                onReBaton={async () => {
                  // Feb 27, 2026 — One-tap "Hand back" from the pickup pill.
                  // Saves the (potentially-updated) draft, then re-shares it
                  // to the same team the original baton came from. No
                  // navigation away from the input view.
                  if (!pickupMeta || !pickupMeta.team_id) {
                    toast.error('No source team on this baton — open Team Board to share.');
                    return;
                  }
                  if (!notes || notes.trim().length < 10) {
                    toast.error('Need a few words of dictation before handing back.');
                    return;
                  }
                  const teamId = pickupMeta.team_id;
                  const teamName = pickupMeta.team_name || 'team';
                  const toastId = toast.loading(`Saving draft and handing back to ${teamName}…`);
                  try {
                    // saveDraft now returns the draft_id (or true). We need an id.
                    const saved = await formActions.saveDraft();
                    const draftId = (typeof saved === 'string' && saved) ? saved : selectedHistoryItem;
                    if (!draftId) {
                      toast.error('Could not save the draft — try again.', { id: toastId });
                      return;
                    }
                    const API = `${window.__WR_API_BASE__}/api`;
                    await axios.post(`${API}/teams/${teamId}/share-note`,
                      { note_id: draftId },
                      { withCredentials: true });
                    toast.success(`Handed back to ${teamName}`, { id: toastId, duration: 3000 });
                    setPickupMeta(null);
                  } catch (e) {
                    console.error('Re-Baton failed:', e);
                    toast.error('Hand-back failed — check team membership.', { id: toastId });
                  }
                }}
                history={history} currentDraftId={selectedHistoryItem}
                onSmartPaste={(text) => {
                  // Auto-extract demographics from a Genesis-style chart paste.
                  const extracted = extractDemographicsFromPaste(text);
                  if (!extracted) return null;
                  if (extracted.age && !patientAge) setPatientAge(extracted.age);
                  if (extracted.gender && !patientGender) setPatientGender(extracted.gender);
                  if (extracted.status && !patientStatus) setPatientStatus(extracted.status);
                  if (extracted.chiefComplaint && !chiefComplaint && setChiefComplaint) setChiefComplaint(extracted.chiefComplaint);
                  return extracted;
                }}
                dosOverride={dosOverride} setDosOverride={setDosOverride}
                medicMode={medicMode} setMedicMode={setMedicMode}
                priorVisitContext={priorVisitContext} setPriorVisitContext={setPriorVisitContext}
                savedInjuries={savedInjuries}
                prepNotes={prepNotes} setPrepNotes={setPrepNotes}
              />
            </EncounterErrorBoundary>
            {!forceSingleColumn && <ColumnScrollNav targetId="input-section" />}
          </div>

          <div id="output-section" className={`${!forceSingleColumn ? 'sm:flex sm:flex-col sm:gap-4' : ''}`}>
            <ICD10Section
              manualIcd10={manualIcd10} setManualIcd10={setManualIcd10}
              autoDetectedCodes={autoDetectedCodes}
              liveIcd10Suggestions={liveIcd10Suggestions}
              primaryIcd10Codes={primaryIcd10Codes} setPrimaryIcd10Codes={setPrimaryIcd10Codes}
              onReorderSuggestions={handleIcd10Reorder} onDeleteSuggestion={handleIcd10Delete}
            />
            <EncounterErrorBoundary>
              <OutputDocumentsPanel
                result={result} effectiveRole={effectiveRole}
                activeOutputTab={activeOutputTab} setActiveOutputTab={setActiveOutputTab}
                documentsReady={documentsReady} setDocumentsReady={setDocumentsReady}
                generationVersion={generationVersion} autoSavedDraft={autoSavedDraft}
                saveDraft={formActions.saveDraft}
                patientAge={patientAge} patientGender={patientGender}
                copyAllDocuments={() => copyAllDocuments(result, noteTitle, manualIcd10, primaryIcd10Codes, providerSignatureBlock)}
                clearAll={formActions.clearAll} generateDocuments={generateDocuments} isGenerating={isGenerating}
                setShowReferralsSection={setShowReferralsSection}
                setGenerateAdminLetter={setGenerateAdminLetter}
                setGenerateLimitations={setGenerateLimitations}
                refreshReferralOnly={refreshReferralOnly}
                refreshAdminLetterOnly={refreshAdminLetterOnly}
                scrubAndRefreshLetter={scrubAndRefreshLetter}
                copiedField={copiedField} handleCopy={handleCopy} CopyButton={CopyButton}
                copyToClipboard={copyToClipboard} charLimit={charLimit} noteTitle={noteTitle}
                exportToWord={exportToWord} exportToPDF={exportToPDF}
                getFilledLetter={getFilledLetter} shareLetter={shareLetter} lastShareUrl={lastShareUrl}
                manualIcd10={manualIcd10} primaryIcd10Codes={primaryIcd10Codes}
                autoDetectedCodes={autoDetectedCodes}
                onReorderIcd10={handleIcd10Reorder} onDeleteIcd10={handleIcd10Delete}
                liveIcd10Suggestions={liveIcd10Suggestions} setPrimaryIcd10Codes={setPrimaryIcd10Codes}
                getAdditionalBillingCodes={getAdditionalBillingCodes}
                providerSignatureBlock={providerSignatureBlock} patientLabel={patientLabel}
                visitType={visitType} setVisitType={setVisitType} encounterType={encounterType}
                virtualModality={virtualModality} setVirtualModality={setVirtualModality}
                dosOverride={dosOverride} setDosOverride={setDosOverride}
                dosTimeOverride={dosTimeOverride} setDosTimeOverride={setDosTimeOverride}
                lateEntry={lateEntry} setLateEntry={setLateEntry}
                quickShareToTeam={quickShareToTeam} autoShareTeamId={autoShareTeamId}
                notes={notes} setNotes={setNotes}
                soldierRank={soldierRank} soldierFirstName={soldierFirstName}
                soldierLastName={soldierLastName} soldierDob={soldierDob}
                soldierUnit={soldierUnit} letterType={letterType}
                occupation={occupation} jobDuties={jobDuties} patientStatus={patientStatus}
                forceSingleColumn={forceSingleColumn}
                icd10PendingConfirm={icd10PendingConfirm}
                confirmIcd10Sequence={confirmIcd10Sequence} dismissIcd10Confirm={dismissIcd10Confirm}
                setResult={setResult}
                setExternalFullSignatureBlock={setFullSignatureBlock}
                loadHistory={loadHistory}
                updateHistoryItem={updateHistoryItem}
              />
            </EncounterErrorBoundary>
            {!forceSingleColumn && <ColumnScrollNav targetId="output-section" />}
          </div>
        </div>
        </>
        )}

        {/* Bottom spacer — prevents the sticky History dock from covering the last bit of content above it */}
        {/* Spacer so content above isn't covered by the fixed-bottom history
            dock. Feb 27, 2026 — bumped from 44 → 96 because the references
            toggle and content below were getting clipped under the dock on
            shorter viewports; the prior 44px only cleared the collapsed
            dock header but left the Resources & References footer toggle
            half-hidden whenever the user scrolled to the very bottom. */}
        <div aria-hidden="true" style={{ height: 96 }} />

        {/* History Panel — Pinned/Frozen to bottom of viewport */}
        {/* Collapsed: ~40px header bar visible · Expanded: max 60vh with internal scroll */}
        <div
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-md shadow-[0_-6px_18px_rgba(0,0,0,0.18)] history-dock-skinny"
          style={{ maxHeight: '60vh', display: 'flex', flexDirection: 'column' }}
          data-testid="history-dock"
        >
          <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
            <HistoryPanel
                history={history} filteredHistory={filteredHistory}
                showHistory={showHistory} setShowHistory={setShowHistory}
                isLoadingHistory={isLoadingHistory} loadHistory={loadHistory}
                loadHistoryItem={formActions.loadHistoryItem} deleteHistoryItem={deleteHistoryItem}
                selectedHistoryItem={selectedHistoryItem}
                historyFilter={historyFilter} setHistoryFilter={setHistoryFilter}
                historySearchQuery={historySearchQuery} setHistorySearchQuery={setHistorySearchQuery}
                dateJump={dateJump} setDateJump={setDateJump}
                sortOrder={sortOrder} setSortOrder={setSortOrder}
                condenseVersions={condenseVersions} setCondenseVersions={setCondenseVersions}
                expandedGroups={expandedGroups} setExpandedGroups={setExpandedGroups}
                isBatchMode={isBatchMode} setIsBatchMode={setIsBatchMode}
                batchSelectedItems={batchSelectedItems} setBatchSelectedItems={setBatchSelectedItems}
                toggleBatchSelect={toggleBatchSelect} selectAllHistory={selectAllHistory}
                exitBatchMode={exitBatchMode} batchCopyNotesAndICD10s={batchCopyNotesAndICD10s}
                batchDeleteItems={batchDeleteItems} batchExportItems={batchExportItems}
                quickExportToday={quickExportToday} quickExportDrafts={quickExportDrafts}
                letterheadLine1={letterheadLine1} letterheadLine2={letterheadLine2} letterheadAddress={letterheadAddress}
                getPendingDraftsCount={getPendingDraftsCount} markDraftsAsCompleted={markDraftsAsCompleted} updateHistoryItem={updateHistoryItem}
                shareLetter={shareLetter} copyToClipboard={copyToClipboard}
                setShowOrdersReview={setShowOrdersReview} setShowWeekendCatchup={setShowWeekendCatchup}
                effectiveRole={effectiveRole} formatDate={formatDate} getLetterTypeLabel={getLetterTypeLabel}
              />
          </div>
        </div>

        {/* Resources & References — Collapsible footer.
            Mar 2, 2026: Provider reported "can't see references and resources
            padding blocking it so it won't expand". Root cause: fixed-position
            floating widgets (KeyboardCheatsheet z-9000, FloatingBugReportButton
            z-40, FloatingTodoWidget z-55, ShortcutLegend z-50) all sit in the
            bottom-right corner. The References toggle sat at `relative z-50`
            which loses to any `fixed` element with z >= 50. Bumped toggle to
            z-[100] and added `sticky bottom-2` so it always floats above the
            floating widgets, plus a `mb-24` breathing room so its expanded
            content isn't clipped. */}
        <Collapsible defaultOpen={false}>
          <CollapsibleTrigger asChild>
            <button className="sticky bottom-2 z-[100] w-full flex items-center justify-center gap-2 py-3 mt-4 mb-1 text-xs text-muted-foreground hover:text-foreground bg-secondary/90 hover:bg-secondary backdrop-blur rounded-md transition-colors border border-border shadow-lg shadow-black/20" data-testid="footer-toggle">
              <ChevronDown className="w-3 h-3" />
              <span className="font-medium">Resources &amp; References</span>
              <ChevronDown className="w-3 h-3" />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="relative z-[90]">
            <div id="reference-section" className="mt-2 pb-4"><Footer /></div>
            <CreatorFooter onNavigate={handleLegalNav} />
            {/* Spacer so the legal/CreatorFooter links sit comfortably above
                the fixed history dock when references are expanded. */}
            <div aria-hidden="true" style={{ height: 120 }} />
          </CollapsibleContent>
        </Collapsible>
      </main>
      )}
      
      <FloatingDictation
        recording={recording}
        isGenerating={isGenerating}
        forceSingleColumn={forceSingleColumn}
        textSizeLevel={textSizeLevel}
      />
      <FloatingMicFAB
        recording={recording}
        effectiveRole={effectiveRole}
      />
      <FloatingTodoWidget effectiveRole={effectiveRole} />

      <FloatingActions
        notes={notes} result={result} isGenerating={isGenerating} generateDocuments={generateDocuments}
        currentMoodIndex={currentMoodIndex} setCurrentMoodIndex={setCurrentMoodIndex}
        forceSingleColumn={forceSingleColumn} toggleColumnLayout={toggleColumnLayout}
        textSizeLevel={textSizeLevel} setTextSizeLevel={setTextSizeLevel}
        includePatientQuotes={includePatientQuotes} setIncludePatientQuotes={setIncludePatientQuotes}
        trackFeature={trackFeature}
        setShowProfileModal={setShowProfileModal}
        effectiveRole={effectiveRole}
        apFormat={apFormat} setApFormat={setApFormat}
        clinicalTone={clinicalTone} setClinicalTone={setClinicalTone}
      />
      
      {showOrdersReview && <OrdersReview onClose={() => setShowOrdersReview(false)} />}
      {showWeekendCatchup && (
        <WeekendCatchup show={showWeekendCatchup} onClose={() => setShowWeekendCatchup(false)}
          userId={user?.user_id || guestId} onLoadNote={formActions.loadHistoryItem} currentNoteId={selectedHistoryItem} />
      )}

      {/* Live Metrics Dashboard modal */}
      {showLiveMetrics && (
        <div
          className="fixed inset-0 z-[90] flex items-start justify-center pt-[8vh] bg-black/70 backdrop-blur-sm px-4"
          onClick={() => setShowLiveMetrics(false)}
          data-testid="live-metrics-backdrop"
        >
          <div
            className="bg-card border border-amber-500/30 rounded-lg shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <LiveMetricsDashboard onClose={() => setShowLiveMetrics(false)} />
          </div>
        </div>
      )}

      {/* Production Hotfix Bundle changelog popup — one-time per device.
          v6.8.46 — `audience` prop filters items so patients only see
          patient-relevant updates and providers only see provider ones. */}
      <HotfixBundleModal
        open={showHotfixBundle}
        onClose={() => setShowHotfixBundle(false)}
        audience={effectiveRole === 'patient' ? 'patient' : 'provider'}
      />

      {/* Floating widget dock — collapsed by default remembered via
          localStorage. Houses BUG REPORT + KEYBOARD SHORTCUTS so they stack
          neatly instead of piling on top of one another and blocking the
          Resources/References toggle. */}
      <FloatingWidgetDock>
        <FloatingBugReportButton
          onClick={() => setShowBugReport(true)}
          hidden={showHotfixBundle || showCommandPalette || showLiveMetrics || showBugReport}
        />
        <KeyboardCheatsheet />
      </FloatingWidgetDock>

      {/* ⌘K Command Palette — global IDE-style action launcher */}
      <CommandPalette
        open={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        actions={[
          // === GENERATION ===
          { id: 'cmd-generate', group: 'Generation', title: 'Generate Documents',
            subtitle: 'Run the multi-document pipeline now', shortcut: 'Ctrl+↵',
            keywords: ['create', 'docs', 'go', 'run'],
            run: () => generateDocuments?.() },
          { id: 'cmd-clear', group: 'Generation', title: 'Clear All',
            subtitle: 'Delete current input + output WITHOUT saving',
            keywords: ['reset', 'discard', 'wipe'],
            run: () => formActions.clearAll?.() },
          { id: 'cmd-next-patient', group: 'Generation', title: 'Next Patient',
            subtitle: 'Save current draft and prepare a fresh encounter',
            keywords: ['save', 'continue', 'new patient'],
            run: () => formActions.clearAll?.(true) },
          { id: 'cmd-save-draft', group: 'Generation', title: 'Save Draft',
            subtitle: 'Persist current work to history',
            keywords: ['persist', 'snapshot'],
            run: () => formActions.saveDraft?.() },

          // === ENCOUNTER TOGGLES ===
          { id: 'cmd-visit-inperson', group: 'Encounter', title: 'Set Visit: In-Person',
            keywords: ['exam', 'live', 'physical'],
            run: () => setVisitType?.('in_person_exam') },
          { id: 'cmd-visit-virtual', group: 'Encounter', title: 'Set Visit: Virtual',
            keywords: ['telehealth', 'phone', 'remote'],
            run: () => setVisitType?.('virtual') },
          { id: 'cmd-pe-on', group: 'Encounter', title: 'Toggle PE Section ON',
            keywords: ['physical exam'],
            run: () => setIncludePE?.(true) },
          { id: 'cmd-pe-off', group: 'Encounter', title: 'Toggle PE Section OFF',
            keywords: ['no exam', 'skip pe'],
            run: () => setIncludePE?.(false) },
          { id: 'cmd-pe-remarkable', group: 'Encounter', title: 'PE: Mark Remarkable',
            keywords: ['findings', 'abnormal'],
            run: () => { setIncludePE?.(true); setPeUnremarkable?.(false); } },
          { id: 'cmd-pe-unremarkable', group: 'Encounter', title: 'PE: Mark Unremarkable',
            keywords: ['normal', 'wnl'],
            run: () => { setIncludePE?.(true); setPeUnremarkable?.(true); } },

          // === NAVIGATION ===
          { id: 'cmd-history', group: 'Navigation', title: 'Open History Panel',
            keywords: ['drafts', 'past', 'all notes'],
            run: () => setShowHistory?.(true) },
          { id: 'cmd-team', group: 'Navigation', title: 'Open Team Board',
            keywords: ['collab', 'share', 'baton'],
            run: () => setShowTeamBoardPanel?.(true) },
          { id: 'cmd-team-collab', group: 'Navigation', title: 'Open Team Collaboration',
            keywords: ['merge', 'pickup'],
            run: () => setShowTeamCollab?.(true) },
          { id: 'cmd-profile', group: 'Navigation', title: 'Open Profile Settings',
            keywords: ['preferences', 'config', 'schedule'],
            run: () => setShowProfileModal?.(true) },
          { id: 'cmd-share', group: 'Navigation', title: 'Open Share Session',
            keywords: ['stream', 'live', 'broadcast'],
            run: () => setShowShareModal?.(true) },
          { id: 'cmd-metrics', group: 'Navigation', title: 'Open Live Metrics Dashboard',
            subtitle: 'Aggregate platform stats — notes generated · minutes saved',
            keywords: ['stats', 'numbers', 'momentum', 'analytics'],
            run: () => setShowLiveMetrics(true) },
          { id: 'cmd-hotfix-bundle', group: 'Navigation', title: "What's New in This Release",
            subtitle: 'Re-open the May 6 production hotfix bundle changelog',
            keywords: ['changelog', 'release notes', 'updates', 'whats new'],
            run: () => setShowHotfixBundle(true) },
          { id: 'cmd-report-bug', group: 'Navigation', title: 'Report a Bug / Feedback',
            subtitle: "Send a bug report or feature request to the developer",
            keywords: ['bug', 'feedback', 'issue', 'report', 'developer', 'broken', 'help'],
            run: () => setShowBugReport(true) },
          { id: 'cmd-speed-chart', group: 'Navigation', title: 'Open Speed Chart (Batch)',
            keywords: ['batch', 'multi', '4 patients'],
            run: () => window.dispatchEvent(new CustomEvent('writeright:open-speed-chart')) },

          // === ROLE SWITCH ===
          { id: 'cmd-role-provider', group: 'Role', title: 'Switch to Provider Mode',
            keywords: ['clinician', 'doc'],
            run: () => { setUserRole?.('provider'); localStorage.setItem('writeright_role', 'provider'); } },
          { id: 'cmd-role-patient', group: 'Role', title: 'Switch to Patient Mode',
            keywords: ['beneficiary', 'soldier', 'self'],
            run: () => { setUserRole?.('patient'); localStorage.setItem('writeright_role', 'patient'); } },

          // === DOT-PHRASES (insert into notes) ===
          ...['.lowdoseCT', '.colonoscopy', '.mammo', '.pap', '.psa', '.aaa',
              '.dm2', '.htn', '.rtd', '.qtrs', '.cacs', '.bhcry',
              '.keyresults', '.PEadd', '.educac', '.eduMESA', '.eduhld',
              '.tbiPRA', '.tbiNSI'].map((phrase) => ({
            id: `cmd-dot-${phrase}`,
            group: 'Dot Phrases',
            title: `Insert ${phrase}`,
            subtitle: 'Append to current notes (cursor at end)',
            keywords: [phrase.replace('.', ''), 'insert', 'template'],
            run: () => {
              try {
                // Trigger the existing dot-phrase expansion path by appending the
                // phrase + space to the notes; NotesInput's input listener will
                // expand it. Fall back to direct setNotes if listener not active.
                setNotes((prev) => `${(prev || '').trimEnd()}\n${phrase} `);
              } catch { /* ignore */ }
            }
          })),
        ]}
      />
      </>
      ) : null}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <SiteGate>
            <BrowserRouter>
              <ErrorBoundary>
                <AppRouter MediScribe={MediScribe} />
              </ErrorBoundary>
            </BrowserRouter>
          </SiteGate>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
